import React, { useState, useEffect } from "react";
import axios from "axios";
import {
  LayoutDashboard, MapPin, Building2, DoorOpen, CalendarCheck,
  LogOut, Plus, Trash2, IndianRupee, Users, Database, Phone, Download, Upload, Shield, ShieldCheck,
  CalendarDays, Sparkles, Compass, UtensilsCrossed, Receipt, Wallet, BarChart3, Settings as SettingsIcon, History, Home as HomeIcon, FileText, Gift
} from "lucide-react";
import BranchPanel from "./Branch";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const TOKEN_KEY = "cv_admin_token";
const BRANCH_TOKEN_KEY = "cv_branch_token";
const BRANCH_META_KEY = "cv_branch_meta";
const SLOTS = ["10:00-13:00", "13:30-16:30", "17:00-20:00", "20:30-23:30"];

const api = axios.create({ baseURL: API });
api.interceptors.request.use((c) => {
  const t = localStorage.getItem(TOKEN_KEY);
  if (t) c.headers["X-Admin-Token"] = t;
  return c;
});

const Btn = ({ children, className = "", ...p }) => (
  <button
    className={`bg-gradient-to-b from-[#e8c98a] to-[#c8a266] text-[#1a0f06] font-semibold px-4 py-2 rounded-md hover:from-[#f1d59c] hover:to-[#d4ac6f] transition text-sm ${className}`}
    {...p}
  >
    {children}
  </button>
);

const Input = (p) => (
  <input
    {...p}
    className={`bg-[#0a0604] border border-[#3a2a14] rounded-md px-3 py-2 text-sm text-white placeholder:text-[#7a6c54] focus:outline-none focus:border-[#d4a574] ${p.className || ""}`}
  />
);

const Select = (p) => (
  <select
    {...p}
    className={`bg-[#0a0604] border border-[#3a2a14] rounded-md px-3 py-2 text-sm text-white focus:outline-none focus:border-[#d4a574] ${p.className || ""}`}
  >
    {p.children}
  </select>
);

function ChangePasswordModal({ open, onClose, onChanged }) {
  const [cur, setCur] = React.useState("");
  const [nw, setNw] = React.useState("");
  const [nw2, setNw2] = React.useState("");
  const [err, setErr] = React.useState("");
  const [busy, setBusy] = React.useState(false);

  if (!open) return null;

  const submit = async (e) => {
    e.preventDefault();
    setErr("");
    if (nw.length < 6) return setErr("New password must be at least 6 characters");
    if (nw !== nw2) return setErr("New passwords do not match");
    setBusy(true);
    try {
      const token = localStorage.getItem(TOKEN_KEY);
      const r = await axios.post(
        `${API}/admin/change-password`,
        { current_password: cur, new_password: nw },
        { headers: { "X-Admin-Token": token } }
      );
      localStorage.setItem(TOKEN_KEY, r.data.token);
      onChanged && onChanged();
      onClose();
      alert("Password updated successfully");
    } catch (e2) {
      setErr(e2?.response?.data?.detail || "Failed to update password");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/60 flex items-center justify-center p-4" data-testid="change-pw-modal">
      <form onSubmit={submit} className="w-full max-w-sm bg-[#1a0f06] border border-[#3a2615] rounded-lg p-6 space-y-3">
        <h3 className="text-[#f4d089] text-lg font-semibold">Change Admin Password</h3>
        <input type="password" placeholder="Current password" value={cur} onChange={(e) => setCur(e.target.value)}
          className="w-full bg-black/40 border border-[#3a2615] rounded px-3 py-2 text-sm text-[#f4d089]"
          data-testid="cur-pw" autoFocus />
        <input type="password" placeholder="New password (min 6 chars)" value={nw} onChange={(e) => setNw(e.target.value)}
          className="w-full bg-black/40 border border-[#3a2615] rounded px-3 py-2 text-sm text-[#f4d089]"
          data-testid="new-pw" />
        <input type="password" placeholder="Confirm new password" value={nw2} onChange={(e) => setNw2(e.target.value)}
          className="w-full bg-black/40 border border-[#3a2615] rounded px-3 py-2 text-sm text-[#f4d089]"
          data-testid="new-pw2" />
        {err && <div className="text-red-400 text-xs">{err}</div>}
        <div className="flex gap-2 justify-end pt-2">
          <button type="button" onClick={onClose} className="px-3 py-2 text-xs text-[#a89878]" data-testid="cancel-change-pw">Cancel</button>
          <button type="submit" disabled={busy} className="px-3 py-2 text-xs bg-[#c8a266] text-[#1a0f06] rounded font-semibold" data-testid="submit-change-pw">
            {busy ? "Saving…" : "Save"}
          </button>
        </div>
      </form>
    </div>
  );
}

/* ---------- Login (multi-role) ---------- */
function Login({ onSuper, onBranch }) {
  const [mode, setMode] = useState("super"); // super | branch
  const [pw, setPw] = useState("");
  const [email, setEmail] = useState("");
  const [bpw, setBpw] = useState("");
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);

  const submitSuper = async (e) => {
    e.preventDefault(); setErr(""); setBusy(true);
    try {
      const r = await axios.post(`${API}/admin/login`, { password: pw });
      localStorage.setItem(TOKEN_KEY, r.data.token);
      onSuper();
    } catch { setErr("Invalid password"); }
    setBusy(false);
  };

  const submitBranch = async (e) => {
    e.preventDefault(); setErr(""); setBusy(true);
    try {
      const r = await axios.post(`${API}/branch/login`, { email, password: bpw });
      localStorage.setItem(BRANCH_TOKEN_KEY, r.data.token);
      localStorage.setItem(BRANCH_META_KEY, JSON.stringify({
        name: r.data.name,
        email: r.data.email,
        branch_id: r.data.branch_id,
        branch_name: r.data.branch_name,
        invoice_prefix: r.data.invoice_prefix,
      }));
      onBranch();
    } catch { setErr("Invalid email or password"); }
    setBusy(false);
  };

  return (
    <div className="min-h-screen bg-[#0a0604] flex items-center justify-center px-4">
      <div className="w-full max-w-sm bg-[#0d0905] border border-[#3a2a14] rounded-xl p-8" data-testid="admin-login-form">
        <div className="text-center mb-6">
          <div className="text-3xl mb-2">🎬</div>
          <h1 className="font-display text-2xl text-[#f4d089]">CineVibe Admin</h1>
          <p className="text-xs text-[#a89878] mt-1">Restricted access</p>
        </div>
        <div className="grid grid-cols-2 gap-2 mb-5 bg-[#0a0604] p-1 rounded-md border border-[#3a2a14]">
          <button
            onClick={() => { setMode("super"); setErr(""); }}
            className={`text-xs py-2 rounded ${mode === "super" ? "bg-[#1a0f06] text-[#f4d089]" : "text-[#a89878]"}`}
            data-testid="role-tab-super"
          >Super Admin</button>
          <button
            onClick={() => { setMode("branch"); setErr(""); }}
            className={`text-xs py-2 rounded ${mode === "branch" ? "bg-[#1a0f06] text-[#f4d089]" : "text-[#a89878]"}`}
            data-testid="role-tab-branch"
          >Branch Manager</button>
        </div>

        {mode === "super" ? (
          <form onSubmit={submitSuper}>
            <label className="text-xs text-[#a89878] uppercase tracking-wider">Password</label>
            <Input
              type="password" value={pw} onChange={(e) => setPw(e.target.value)}
              placeholder="Enter admin password" className="w-full mt-1 mb-3"
              data-testid="admin-password-input"
            />
            {err && <div className="text-red-400 text-xs mb-3" data-testid="login-error">{err}</div>}
            <Btn className="w-full" type="submit" disabled={busy} data-testid="admin-login-btn">{busy ? "..." : "Login as Super Admin"}</Btn>
          </form>
        ) : (
          <form onSubmit={submitBranch}>
            <label className="text-xs text-[#a89878] uppercase tracking-wider">Email</label>
            <Input
              type="email" value={email} onChange={(e) => setEmail(e.target.value)}
              placeholder="manager@cinevibe.in" className="w-full mt-1 mb-3"
              data-testid="branch-email-input"
            />
            <label className="text-xs text-[#a89878] uppercase tracking-wider">Password</label>
            <Input
              type="password" value={bpw} onChange={(e) => setBpw(e.target.value)}
              placeholder="••••••••" className="w-full mt-1 mb-3"
              data-testid="branch-password-input"
            />
            {err && <div className="text-red-400 text-xs mb-3" data-testid="login-error">{err}</div>}
            <Btn className="w-full" type="submit" disabled={busy} data-testid="branch-login-btn">{busy ? "..." : "Login as Branch Manager"}</Btn>
          </form>
        )}
      </div>
    </div>
  );
}

/* ---------- Dashboard ---------- */
function StatsView({ cities, branches, setTab }) {
  const [stats, setStats] = useState(null);
  const [filter, setFilter] = useState({ city_id: "", branch_id: "", date: "" });

  const load = async () => {
    const params = Object.fromEntries(Object.entries(filter).filter(([_, v]) => v));
    const r = await api.get("/admin/dashboard", { params });
    setStats(r.data);
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [filter]);

  const filteredBranches = branches.filter((b) => !filter.city_id || b.city_id === filter.city_id);
  const sc = stats?.status_counts || {};

  return (
    <div data-testid="stats-view">
      <h2 className="font-display text-2xl text-white mb-1">Dashboard</h2>
      <p className="text-xs text-[#a89878] mb-5">Today's snapshot · upcoming 24h · status overview</p>

      <div className="flex flex-wrap gap-3 mb-5">
        <Select value={filter.city_id} onChange={(e) => setFilter({ ...filter, city_id: e.target.value, branch_id: "" })} data-testid="filter-city">
          <option value="">All Cities</option>
          {cities.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
        </Select>
        <Select value={filter.branch_id} onChange={(e) => setFilter({ ...filter, branch_id: e.target.value })} data-testid="filter-branch">
          <option value="">All Branches</option>
          {filteredBranches.map((b) => <option key={b.id} value={b.id}>{b.name}</option>)}
        </Select>
        <Input type="date" value={filter.date} onChange={(e) => setFilter({ ...filter, date: e.target.value })} data-testid="filter-date" />
        <div className="ml-auto flex gap-2">
          <Btn onClick={() => setTab && setTab("bookings")} data-testid="quick-add-booking">Add Booking</Btn>
          <Btn onClick={() => setTab && setTab("calendar")} data-testid="quick-slots">Manage Slots</Btn>
          <Btn onClick={() => setTab && setTab("billing")} data-testid="quick-invoice">Invoices</Btn>
        </div>
      </div>

      {stats && (
        <>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-5">
            <StatCard label="Bookings (Today)" value={stats.today_bookings} testid="stat-today-bookings" />
            <StatCard label="Revenue (Today)" value={`₹${(stats.today_revenue||0).toLocaleString()}`} icon={<IndianRupee size={16} />} testid="stat-today-revenue" />
            <StatCard label="Active Branches" value={stats.active_branches} testid="stat-active-branches" />
            <StatCard label="Total Bookings" value={stats.total_bookings} testid="stat-bookings" />
          </div>

          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
            <StatCard label="Confirmed" value={sc.confirmed || 0} testid="stat-confirmed" />
            <StatCard label="Completed" value={sc.completed || 0} testid="stat-completed" />
            <StatCard label="Cancelled" value={sc.cancelled || 0} testid="stat-cancelled" />
            <StatCard label="Pending" value={sc.pending || 0} testid="stat-pending" />
          </div>

          <div className="bg-[#0d0905] border border-[#3a2a14] rounded-lg p-5">
            <div className="font-display text-[#f4d089] mb-3">Upcoming Bookings (next 24h)</div>
            {(stats.upcoming || []).length === 0 ? (
              <div className="text-[#a89878] text-sm">No upcoming bookings.</div>
            ) : (
              <Table headers={["Date", "Slot", "Customer", "Occasion", "Notes", "Guests", "Amount"]}>
                {stats.upcoming.map((b) => (
                  <tr key={b.id} className="border-t border-[#3a2a14]">
                    <td className="py-3 px-3 text-white">{b.date}</td>
                    <td className="text-[#bfb393]">{b.slot}</td>
                    <td className="text-white">{b.customer_name} <span className="text-xs text-[#a89878]">{b.customer_phone}</span></td>
                    <td className="text-xs">{b.occasion ? <span className="px-2 py-0.5 rounded bg-[#2a1a0a] text-[#f4d089]">{b.occasion}</span> : <span className="text-[#a89878]">—</span>}</td>
                    <td className="text-[#bfb393] text-xs max-w-[180px] truncate" title={b.special_request || ""}>{b.special_request || "—"}</td>
                    <td className="text-[#bfb393]">{b.guests}</td>
                    <td className="text-[#f4d089]">₹{b.amount}</td>
                  </tr>
                ))}
              </Table>
            )}
          </div>
        </>
      )}
    </div>
  );
}

const StatCard = ({ label, value, icon, testid }) => (
  <div className="bg-[#0d0905] border border-[#3a2a14] rounded-lg p-4" data-testid={testid}>
    <div className="text-[10px] uppercase tracking-wider text-[#a89878] flex items-center gap-1">
      {icon} {label}
    </div>
    <div className="font-display text-2xl text-[#f4d089] mt-1">{value}</div>
  </div>
);

/* ---------- Cities ---------- */
function CitiesView({ cities, reload }) {
  const [name, setName] = useState("");
  const add = async () => {
    if (!name.trim()) return;
    await api.post("/admin/cities", { name: name.trim() });
    setName(""); reload();
  };
  const del = async (id) => {
    if (!window.confirm("Delete this city?")) return;
    await api.delete(`/admin/cities/${id}`); reload();
  };
  return (
    <div data-testid="cities-view">
      <h2 className="font-display text-2xl text-white mb-4">Cities</h2>
      <div className="flex gap-2 mb-5">
        <Input value={name} onChange={(e) => setName(e.target.value)} placeholder="City name (e.g., Delhi NCR)" data-testid="new-city-input" />
        <Btn onClick={add} data-testid="add-city-btn"><Plus size={14} className="inline mr-1" />Add</Btn>
      </div>
      <Table headers={["Name", ""]}>
        {cities.map((c) => (
          <tr key={c.id} className="border-t border-[#3a2a14]">
            <td className="py-3 px-3 text-white">{c.name}</td>
            <td className="text-right pr-3"><button onClick={() => del(c.id)} className="text-red-400" data-testid={`del-city-${c.id}`}><Trash2 size={14} /></button></td>
          </tr>
        ))}
      </Table>
    </div>
  );
}

/* ---------- Branches ---------- */
function BranchesView({ cities, branches, reload }) {
  const [f, setF] = useState({ city_id: "", name: "", address: "", invoice_prefix: "BR", manager_email: "", petpooja_menu_url: "", food_phone: "", map_link: "", video_link: "", image_url: "", capacity: 0, slots: [] });
  const [editing, setEditing] = useState(null); // branch.id
  const [editVal, setEditVal] = useState({ petpooja_menu_url: "", food_phone: "", map_link: "", video_link: "", image_url: "", capacity: 0, slots: [] });
  const add = async () => {
    if (!f.city_id || !f.name.trim()) return alert("City and name required");
    await api.post("/admin/branches", { ...f, capacity: Number(f.capacity) || 0 });
    setF({ city_id: "", name: "", address: "", invoice_prefix: "BR", manager_email: "", petpooja_menu_url: "", food_phone: "", map_link: "", video_link: "", image_url: "", capacity: 0 });
    reload();
  };
  const del = async (id) => {
    if (!window.confirm("Delete branch?")) return;
    await api.delete(`/admin/branches/${id}`); reload();
  };
  const startEdit = (b) => {
  setEditing(b.id);
  setEditVal({
    petpooja_menu_url: b.petpooja_menu_url || "",
    food_phone: b.food_phone || "",
    map_link: b.map_link || "",
    video_link: b.video_link || "",
    image_url: b.image_url || "",
    capacity: b.capacity || 0,
    slots: Array.isArray(b.slots) ? b.slots : [],
  });
};
  const saveEdit = async (id) => {
  const cleanSlots = (editVal.slots || []).map(s => (s || "").trim()).filter(Boolean);
  await api.patch(`/admin/branches/${id}`, {
    ...editVal,
    capacity: Number(editVal.capacity) || 0,
    slots: cleanSlots,
  });
  setEditing(null);
  reload();
};
  const cityName = (id) => cities.find((c) => c.id === id)?.name || "—";
  return (
    <div data-testid="branches-view">
      <h2 className="font-display text-2xl text-white mb-4">Branches</h2>
      <div className="grid grid-cols-1 md:grid-cols-7 gap-2 mb-3">
        <Select value={f.city_id} onChange={(e) => setF({ ...f, city_id: e.target.value })} data-testid="branch-city">
          <option value="">Select City</option>
          {cities.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
        </Select>
        <Input value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} placeholder="Branch name" data-testid="branch-name" />
        <Input value={f.address} onChange={(e) => setF({ ...f, address: e.target.value })} placeholder="Address" />
        <Input value={f.invoice_prefix} onChange={(e) => setF({ ...f, invoice_prefix: e.target.value.toUpperCase() })} placeholder="Prefix (DEL)" data-testid="branch-prefix" />
        <Input value={f.manager_email} onChange={(e) => setF({ ...f, manager_email: e.target.value })} placeholder="Manager email" />
        <Input value={f.food_phone} onChange={(e) => setF({ ...f, food_phone: e.target.value })} placeholder="Café phone" data-testid="branch-food-phone" />
        <Btn onClick={add} data-testid="add-branch-btn"><Plus size={14} className="inline mr-1" />Add</Btn>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-4 gap-2 mb-5">
        <Input value={f.petpooja_menu_url} onChange={(e) => setF({ ...f, petpooja_menu_url: e.target.value })} placeholder="Billing Software menu URL (https://…)" data-testid="branch-petpooja-url" />
        <Input value={f.map_link} onChange={(e) => setF({ ...f, map_link: e.target.value })} placeholder="Google Maps link / embed URL" data-testid="branch-map-link" />
        <Input value={f.video_link} onChange={(e) => setF({ ...f, video_link: e.target.value })} placeholder="Venue video link (YouTube/MP4)" data-testid="branch-video-link" />
        <Input value={f.image_url} onChange={(e) => setF({ ...f, image_url: e.target.value })} placeholder="Branch photo URL (https://…)" data-testid="branch-image-url" />
        <Input type="number" min="0" value={f.capacity} onChange={(e) => setF({ ...f, capacity: e.target.value })} placeholder="Capacity (people)" data-testid="branch-capacity" />
      </div>
      <Table headers={["City", "Branch", "Prefix", "Map / Video / Capacity", "Billing Software Menu URL", "Café Phone", ""]}>
        {branches.map((b) => (
          <tr key={b.id} className="border-t border-[#3a2a14]" data-testid={`branch-row-${b.id}`}>
            <td className="py-3 px-3 text-white">{cityName(b.city_id)}</td>
            <td className="text-white">{b.name} <span className="text-xs text-[#a89878]">{b.address}</span></td>
            <td className="text-[#f4d089]">{b.invoice_prefix}</td>
            <td className="text-[#bfb393] text-xs max-w-[280px] align-top">
              {editing === b.id ? (
                <div className="space-y-1.5">
                  <Input
                    value={editVal.map_link}
                    onChange={(e) => setEditVal({ ...editVal, map_link: e.target.value })}
                    placeholder="Google Maps link"
                    data-testid={`edit-map-link-${b.id}`}
                  />
                  <Input
                    value={editVal.video_link}
                    onChange={(e) => setEditVal({ ...editVal, video_link: e.target.value })}
                    placeholder="Venue video link"
                    data-testid={`edit-video-link-${b.id}`}
                  />
                  <Input
                    value={editVal.image_url}
                    onChange={(e) => setEditVal({ ...editVal, image_url: e.target.value })}
                    placeholder="Branch photo URL (https://…)"
                    data-testid={`edit-image-url-${b.id}`}
                  />
                  <Input
                    type="number"
                    min="0"
                    value={editVal.capacity}
                    onChange={(e) => setEditVal({ ...editVal, capacity: e.target.value })}
                    placeholder="Capacity"
                    data-testid={`edit-capacity-${b.id}`}
                  />

                    <div className="border-t border-[#3a2a14] pt-2 mt-2">
  <div className="text-[10px] uppercase tracking-wider text-[#f4d089] mb-1">
    Slot Times <span className="text-[#a89878] normal-case">(format: HH:MM-HH:MM)</span>
  </div>
  {(editVal.slots || []).map((s, i) => (
    <div key={i} className="flex gap-1 mb-1">
      <Input
        value={s}
        onChange={(e) => {
          const arr = [...editVal.slots];
          arr[i] = e.target.value;
          setEditVal({ ...editVal, slots: arr });
        }}
        placeholder="10:00-13:00"
        data-testid={`edit-slot-${b.id}-${i}`}
      />
      <button
        type="button"
        onClick={() => setEditVal({ ...editVal, slots: editVal.slots.filter((_, k) => k !== i) })}
        className="text-red-400 text-xs px-2"
      >✕</button>
    </div>
  ))}
  <button
    type="button"
    onClick={() => setEditVal({ ...editVal, slots: [...(editVal.slots || []), ""] })}
    className="text-[#7ad99c] text-xs"
    data-testid={`add-slot-${b.id}`}
  >+ Add slot</button>
  <div className="text-[10px] text-[#a89878] mt-1">Leave empty to use default 4 slots.</div>
</div>
                </div>
              ) : (
                <div className="space-y-1">
                  <div>
                    Map:{" "}
                    {b.map_link
                      ? <a href={b.map_link} target="_blank" rel="noreferrer" className="text-[#7ad99c] underline break-all">{b.map_link.slice(0, 32)}…</a>
                      : <span className="italic text-[#a89878]">— not set —</span>}
                  </div>
                  <div>
                    Video:{" "}
                    {b.video_link
                      ? <a href={b.video_link} target="_blank" rel="noreferrer" className="text-[#7ad99c] underline break-all">{b.video_link.slice(0, 32)}…</a>
                      : <span className="italic text-[#a89878]">— not set —</span>}
                  </div>
                  <div>Capacity: <span className="text-[#f4d089]">{b.capacity || 0}</span> people</div>
                </div>
              )}
            </td>
            <td className="text-[#bfb393] text-xs max-w-[260px]">
              {editing === b.id ? (
                <Input
                  value={editVal.petpooja_menu_url}
                  onChange={(e) => setEditVal({ ...editVal, petpooja_menu_url: e.target.value })}
                  placeholder="https://…"
                  data-testid={`edit-menu-url-${b.id}`}
                />
              ) : b.petpooja_menu_url ? (
                <a href={b.petpooja_menu_url} target="_blank" rel="noreferrer" className="text-[#7ad99c] underline break-all">{b.petpooja_menu_url}</a>
              ) : <span className="italic text-[#a89878]">— not set —</span>}
            </td>
            <td className="text-[#bfb393] text-xs">
              {editing === b.id ? (
                <Input
                  value={editVal.food_phone}
                  onChange={(e) => setEditVal({ ...editVal, food_phone: e.target.value })}
                  placeholder="Phone"
                  data-testid={`edit-food-phone-${b.id}`}
                />
              ) : (b.food_phone || "—")}
            </td>
            <td className="text-right pr-3 whitespace-nowrap align-top">
              {editing === b.id ? (
                <>
                  <button onClick={() => saveEdit(b.id)} className="text-[#7ad99c] mr-3 text-xs" data-testid={`save-branch-${b.id}`}>Save</button>
                  <button onClick={() => setEditing(null)} className="text-[#a89878] mr-3 text-xs">Cancel</button>
                </>
              ) : (
                <button onClick={() => startEdit(b)} className="text-[#f4d089] mr-3 text-xs" data-testid={`edit-branch-${b.id}`}>Edit</button>
              )}
              <button onClick={() => del(b.id)} className="text-red-400"><Trash2 size={14} /></button>
            </td>
          </tr>
        ))}
      </Table>
    </div>
  );
}

/* ---------- Rooms ---------- */
function RoomsView({ branches, rooms, reload }) {
  const [f, setF] = useState({ branch_id: "", name: "", capacity: 6, price_per_slot: 2499, min_guests: 2, extra_guest_price: 0, image_url: "", images: [], video_link: "", payment_mode: "both", slots: [] });
  const [editing, setEditing] = useState(null);
  const [ev, setEv] = useState({ name: "", capacity: 6, price_per_slot: 0, min_guests: 2, extra_guest_price: 0, image_url: "", images: [], video_link: "", payment_mode: "both", slots: [] });
  const filesToBase64 = (fileList) => Promise.all(
    Array.from(fileList).map((file) => new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => resolve(reader.result); // data:image/...;base64,xxx
      reader.onerror = reject;
      reader.readAsDataURL(file);
    }))
  );
  const add = async () => {
    if (!f.branch_id || !f.name.trim()) return alert("Branch and name required");
    const cleanCreateSlots = (f.slots || []).map(s => (s || "").trim()).filter(Boolean);
    await api.post("/admin/rooms", { ...f, capacity: +f.capacity, price_per_slot: +f.price_per_slot, min_guests: +f.min_guests, extra_guest_price: +f.extra_guest_price, image_url: f.image_url || "", images: f.images || [], video_link: f.video_link || "", payment_mode: f.payment_mode || "both", slots: cleanCreateSlots });
    if (f.branch_video_link !== undefined) {
      try { await api.patch(`/admin/branches/${f.branch_id}`, { video_link: f.branch_video_link || "" }); } catch (_) {}
    }
    setF({ branch_id: "", name: "", capacity: 6, price_per_slot: 2499, min_guests: 2, extra_guest_price: 0, image_url: "", images: [], video_link: "", branch_video_link: "", payment_mode: "both", slots: [] });
    reload();
  };
  const del = async (id) => {
    if (!window.confirm("Delete room?")) return;
    await api.delete(`/admin/rooms/${id}`); reload();
  };
  const startEdit = (r) => {
    const br = branches.find((b) => b.id === r.branch_id);
    setEditing(r.id);
    setEv({ name: r.name, capacity: r.capacity, price_per_slot: r.price_per_slot, min_guests: r.min_guests ?? 2, extra_guest_price: r.extra_guest_price ?? 0, image_url: r.image_url || "", images: r.images || [], video_link: r.video_link || "", branch_video_link: br?.video_link || "", _branch_id: r.branch_id, payment_mode: r.payment_mode || "both", slots: Array.isArray(r.slots) ? r.slots : [], slot_discounts: r.slot_discounts || {} });
  };
  const saveEdit = async (id) => {
    const { branch_video_link, _branch_id, ...roomFields } = ev;
    const cleanRoomSlots = (ev.slots || []).map(s => (s || "").trim()).filter(Boolean);
    // Only keep discounts for slots that still exist
    const cleanDiscounts = {};
    for (const s of cleanRoomSlots) {
      const v = +(ev.slot_discounts || {})[s] || 0;
      if (v > 0) cleanDiscounts[s] = v;
    }
    await api.patch(`/admin/rooms/${id}`, { ...roomFields, capacity: +ev.capacity, price_per_slot: +ev.price_per_slot, min_guests: +ev.min_guests, extra_guest_price: +ev.extra_guest_price, image_url: ev.image_url || "", images: ev.images || [], video_link: ev.video_link || "", payment_mode: ev.payment_mode || "both", slots: cleanRoomSlots, slot_discounts: cleanDiscounts });
    if (_branch_id) {
      try { await api.patch(`/admin/branches/${_branch_id}`, { video_link: branch_video_link || "" }); } catch (_) {}
    }
    setEditing(null); reload();
  };
  const toggleStatus = async (r) => {
    await api.patch(`/admin/rooms/${r.id}`, { status: r.status === "inactive" ? "active" : "inactive" });
    reload();
  };
  const branchName = (id) => branches.find((b) => b.id === id)?.name || "—";
  return (
    <div data-testid="rooms-view">
      <h2 className="font-display text-2xl text-white mb-4">Rooms</h2>
      <div className="grid grid-cols-1 md:grid-cols-9 gap-2 mb-5">
        <Select value={f.branch_id} onChange={(e) => {
          const bid = e.target.value;
          const br = branches.find((b) => b.id === bid);
          setF({ ...f, branch_id: bid, branch_video_link: br?.video_link || "" });
        }}>
          <option value="">Select Branch</option>
          {branches.map((b) => <option key={b.id} value={b.id}>{b.name}</option>)}
        </Select>
        <Input value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} placeholder="Room name" />
        <Input type="number" value={f.capacity} onChange={(e) => setF({ ...f, capacity: e.target.value })} placeholder="Capacity" />
        <Input type="number" value={f.price_per_slot} onChange={(e) => setF({ ...f, price_per_slot: e.target.value })} placeholder="Price/slot" />
        <Input type="number" value={f.min_guests} onChange={(e) => setF({ ...f, min_guests: e.target.value })} placeholder="Min guests" data-testid="room-min-guests" />
        <Input type="number" value={f.extra_guest_price} onChange={(e) => setF({ ...f, extra_guest_price: e.target.value })} placeholder="Extra guest ₹" data-testid="room-extra-price" />
        <div className="md:col-span-2">
  <label className="block text-[11px] text-[#a89878] mb-1">Theatre images (multiple)</label>
  <input
    type="file"
    accept="image/*"
    multiple
    data-testid="room-image-files"
    onChange={async (e) => {
      const arr = await filesToBase64(e.target.files);
      setF({ ...f, images: [...(f.images || []), ...arr] });
      e.target.value = ""; // reset so same file can be re-picked later
    }}
    className="text-xs text-[#bfb393]"
  />
  {(f.images || []).length > 0 && (
    <div className="flex flex-wrap gap-2 mt-2">
      {(f.images || []).map((src, i) => (
        <div key={i} className="relative">
          <img src={src} alt="" className="w-16 h-16 object-cover rounded border border-[#3a2a14]" />
          <button
            type="button"
            onClick={() => setF({ ...f, images: f.images.filter((_, j) => j !== i) })}
            className="absolute -top-1 -right-1 bg-red-500 text-white text-[10px] w-4 h-4 rounded-full leading-none"
            data-testid={`room-image-remove-${i}`}
          >×</button>
        </div>
      ))}
    </div>
  )}
</div>
        <Input value={f.video_link} onChange={(e) => setF({ ...f, video_link: e.target.value })} placeholder="Theatre video link" data-testid="room-video-link" />
        <Input value={f.branch_video_link || ""} onChange={(e) => setF({ ...f, branch_video_link: e.target.value })} placeholder="Branch video link" data-testid="room-branch-video-link" />
        <Select value={f.payment_mode || "both"} onChange={(e) => setF({ ...f, payment_mode: e.target.value })} data-testid="room-payment-mode">
          <option value="both">Advance + Full</option>
          <option value="full_only">Full Only</option>
        </Select>
        <Btn onClick={add}><Plus size={14} className="inline mr-1" />Add</Btn>
      </div>
      <Table headers={["Branch", "Room", "Capacity", "Price/Slot", "Min Guests", "Extra/Guest", "Image", "Video", "Payment", "Status", ""]}>
        {rooms.map((r) => (
          <React.Fragment key={r.id}>
          <tr className="border-t border-[#3a2a14]" data-testid={`room-row-${r.id}`}>
            <td className="py-3 px-3 text-white">{branchName(r.branch_id)}</td>
            <td className="text-white">
              {editing === r.id ? <Input value={ev.name} onChange={(e) => setEv({ ...ev, name: e.target.value })} /> : r.name}
            </td>
            <td className="text-[#bfb393]">
              {editing === r.id ? <Input type="number" value={ev.capacity} onChange={(e) => setEv({ ...ev, capacity: e.target.value })} className="w-20" /> : r.capacity}
            </td>
            <td className="text-[#f4d089]">
              {editing === r.id ? <Input type="number" value={ev.price_per_slot} onChange={(e) => setEv({ ...ev, price_per_slot: e.target.value })} className="w-24" /> : `₹${r.price_per_slot}`}
            </td>
            <td className="text-[#bfb393]">
              {editing === r.id ? <Input type="number" value={ev.min_guests} onChange={(e) => setEv({ ...ev, min_guests: e.target.value })} className="w-20" /> : (r.min_guests ?? 2)}
            </td>
            <td className="text-[#f4d089]">
              {editing === r.id ? <Input type="number" value={ev.extra_guest_price} onChange={(e) => setEv({ ...ev, extra_guest_price: e.target.value })} className="w-24" /> : `₹${r.extra_guest_price ?? 0}`}
            </td>
            <td className="text-[#bfb393]">
  {editing === r.id ? (
    <div>
      <input
        type="file"
        accept="image/*"
        multiple
        onChange={async (e) => {
          const arr = await filesToBase64(e.target.files);

          setEv({
            ...ev,
            images: [...(ev.images || []), ...arr]
          });

          e.target.value = "";
        }}
        className="text-[10px] text-[#bfb393]"
      />

      <div className="flex flex-wrap gap-1 mt-1">
        {(ev.images || []).map((src, i) => (
          <div key={i} className="relative">
            <img
              src={src}
              alt=""
              className="w-10 h-10 object-cover rounded"
            />

            <button
              type="button"
              onClick={() =>
                setEv({
                  ...ev,
                  images: ev.images.filter((_, j) => j !== i)
                })
              }
              className="absolute -top-1 -right-1 bg-red-500 text-white text-[9px] w-3.5 h-3.5 rounded-full leading-none"
            >
              ×
            </button>
          </div>
        ))}
      </div>
    </div>
  ) : (
    <div className="flex gap-1">
      {((r.images && r.images.length)
        ? r.images
        : (r.image_url ? [r.image_url] : [])
      )
        .slice(0, 3)
        .map((src, i) => (
          <img
            key={i}
            src={src}
            alt=""
            className="w-10 h-10 object-cover rounded"
          />
        ))}

      {(r.images?.length || 0) > 3 && (
        <span className="text-[10px] text-[#a89878]">
          +{r.images.length - 3}
        </span>
      )}

      {(!r.images || r.images.length === 0) &&
        !r.image_url &&
        "—"}
    </div>
  )}
</td>
            <td className="text-[#bfb393]">
              {editing === r.id ? (
                <div className="flex flex-col gap-1">
                  <Input value={ev.video_link} onChange={(e) => setEv({ ...ev, video_link: e.target.value })} placeholder="Theatre video" className="w-40" />
                  <Input value={ev.branch_video_link || ""} onChange={(e) => setEv({ ...ev, branch_video_link: e.target.value })} placeholder="Branch video" className="w-40" data-testid={`edit-branch-video-${r.id}`} />
                </div>
              ) : (
                <div className="flex flex-col gap-0.5 text-xs">
                  {r.video_link ? <a href={r.video_link} target="_blank" rel="noreferrer" className="text-[#7ad99c] underline">theatre ▶</a> : <span className="text-[#a89878]">no theatre</span>}
                  {(() => {
                    const br = branches.find((b) => b.id === r.branch_id);
                    return br?.video_link ? <a href={br.video_link} target="_blank" rel="noreferrer" className="text-[#7ad99c] underline">branch ▶</a> : <span className="text-[#a89878]">no branch</span>;
                  })()}
                </div>
              )}
            </td>
            <td className="text-[#bfb393]">
              {editing === r.id ? (
                <select
                  value={ev.payment_mode || "both"}
                  onChange={(e) => setEv({ ...ev, payment_mode: e.target.value })}
                  className="bg-[#0a0604] border border-[#3a2a14] rounded px-2 py-1 text-xs text-white"
                  data-testid={`room-payment-mode-${r.id}`}
                >
                  <option value="both">Advance + Full</option>
                  <option value="full_only">Full Only</option>
                </select>
              ) : (
                (r.payment_mode || "both") === "full_only" ? "Full Only" : "Advance + Full"
              )}
            </td>
            <td>
              <button onClick={() => toggleStatus(r)} className={`text-xs px-2 py-1 rounded ${(r.status || "active") === "active" ? "bg-[#1a3a22] text-[#7ad99c]" : "bg-[#3a1414] text-red-300"}`} data-testid={`toggle-room-${r.id}`}>
                {(r.status || "active") === "active" ? "Active" : "Inactive"}
              </button>
            </td>
            <td className="text-right pr-3 whitespace-nowrap">
              {editing === r.id ? (
                <>
                  <button onClick={() => saveEdit(r.id)} className="text-[#7ad99c] mr-2 text-xs" data-testid={`save-room-${r.id}`}>Save</button>
                  <button onClick={() => setEditing(null)} className="text-[#a89878] mr-2 text-xs">Cancel</button>
                </>
              ) : (
                <button onClick={() => startEdit(r)} className="text-[#f4d089] mr-3 text-xs" data-testid={`edit-room-${r.id}`}>Edit</button>
              )}
              <button onClick={() => del(r.id)} className="text-red-400"><Trash2 size={14} /></button>
            </td>
          </tr>
          {editing === r.id && (
            <tr className="bg-[#0d0905] border-t border-[#3a2a14]" data-testid={`room-slots-editor-${r.id}`}>
              <td colSpan="11" className="p-3">
                <div className="text-xs uppercase tracking-wider text-[#f4d089] mb-2">
                  Slot Times for this theatre <span className="text-[#a89878] normal-case">(format: HH:MM-HH:MM)</span>
                </div>
                {(ev.slots || []).map((s, i) => (
                  <div key={i} className="flex items-center gap-2 mb-1">
                    <Input
                      value={s}
                      onChange={(e) => {
                        const arr = [...ev.slots];
                        arr[i] = e.target.value;
                        setEv({ ...ev, slots: arr });
                      }}
                      placeholder="10:00-13:00"
                      className="w-48"
                      data-testid={`edit-room-slot-${r.id}-${i}`}
                    />
                    <Input
                      type="number"
                      min="0"
                      value={(ev.slot_discounts || {})[s] || ""}
                      onChange={(e) => {
                        const sd = { ...(ev.slot_discounts || {}) };
                        const val = e.target.value;
                        if (!val || +val <= 0) { delete sd[s]; }
                        else { sd[s] = +val; }
                        setEv({ ...ev, slot_discounts: sd });
                      }}
                      placeholder="Discount ₹ (e.g. 850)"
                      className="w-40"
                      data-testid={`edit-room-slot-discount-${r.id}-${i}`}
                    />
                    <button
                      onClick={() => {
                        const sd = { ...(ev.slot_discounts || {}) };
                        delete sd[s];
                        setEv({ ...ev, slots: ev.slots.filter((_, k) => k !== i), slot_discounts: sd });
                      }}
                      className="text-xs text-red-400 underline"
                      data-testid={`delete-room-slot-${r.id}-${i}`}
                    >Delete</button>
                  </div>
                ))}
                <button
                  onClick={() => setEv({ ...ev, slots: [...(ev.slots || []), ""] })}
                  className="text-xs text-[#7ad99c] underline mt-1"
                  data-testid={`add-room-slot-${r.id}`}
                >+ Add slot</button>
                <div className="text-[10px] text-[#a89878] mt-1">
                  Leave empty to fall back to the branch's slot times (or the 4 default slots).
                </div>
              </td>
            </tr>
          )}
          </React.Fragment>
        ))}
      </Table>
    </div>
  );
}

/* ---------- Bookings ---------- */
function BookingsView({ cities, branches, rooms }) {
  const [list, setList] = useState([]);
  const [filter, setFilter] = useState({ city_id: "", branch_id: "", date: "" });
  const [open, setOpen] = useState(false);
  const [nb, setNb] = useState({
    branch_id: "", room_id: "", customer_name: "", customer_phone: "",
    date: "", slot: "10:00-13:00", guests: 2, amount: 0,
    advance_amount: 750,
    balance_amount: 0,
  });

  const load = async () => {
    const params = Object.fromEntries(Object.entries(filter).filter(([_, v]) => v));
    const r = await api.get("/admin/bookings", { params });
    setList(r.data);
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [filter]);

  const filteredBranches = branches.filter((b) => !filter.city_id || b.city_id === filter.city_id);
  const nbRooms = rooms.filter((r) => r.branch_id === nb.branch_id);

  const submit = async () => {
    if (!nb.branch_id || !nb.room_id || !nb.customer_name || !nb.customer_phone || !nb.date || !nb.slot) {
      return alert("Branch, room, name, phone, date and slot are required");
    }

    const amount = +nb.amount || 0;
    const advance = +nb.advance_amount || 0;
    const balance = +nb.balance_amount || 0;

    if (advance < 750) {
      return alert("Advance amount must be at least ₹750");
    }
    if (advance > amount) {
      return alert("Advance amount cannot be greater than total amount");
    }
    if (balance < 0) {
      return alert("Balance amount cannot be negative");
    }

    const isFullyPaid = balance === 0 && advance >= amount;
    const payload = {
      branch_id: nb.branch_id,
      room_id: nb.room_id,
      customer_name: nb.customer_name,
      customer_phone: nb.customer_phone,
      date: nb.date,
      slot: nb.slot,
      guests: +nb.guests,
      amount,
      payment_type: isFullyPaid ? "full" : "advance",
      payment_status: isFullyPaid ? "paid" : "partial",
      advance_amount: advance,
      paid_amount: advance,
      balance_due: balance,
    };

    try {
      await api.post("/admin/bookings", payload);
    } catch (err) {
      return alert("Save failed: " + (err?.response?.data?.detail || err.message));
    }
    setOpen(false);
    setNb({
      branch_id: "", room_id: "", customer_name: "", customer_phone: "",
      date: "", slot: "10:00-13:00", guests: 2, amount: 0,
      advance_amount: 750, balance_amount: 0,
    });
    load();
  };

  const branchName = (id) => branches.find((b) => b.id === id)?.name || "—";
  const roomName = (id) => rooms.find((r) => r.id === id)?.name || "—";

  const updateStatus = async (id, status) => {
    if (status === "cancelled" && !window.confirm("Cancel this booking? Slot will be released.")) return;
    await api.patch(`/admin/bookings/${id}`, { status });
    load();
  };

  const markBalancePaid = async (b) => {
    const due = Number(b.balance_due || 0) > 0
      ? Number(b.balance_due)
      : Number(b.amount || 0) - Number(b.paid_amount || 0);
    if (!window.confirm(`Mark ₹${due} as collected at venue?`)) return;
    await api.patch(`/admin/bookings/${b.id}`, {
      paid_amount: Number(b.amount || 0),
      balance_due: 0,
      payment_status: "paid",
    });
    load();
  };

  const shareWA = (b) => {
    const branch = branchName(b.branch_id);
    const msg = `Hi ${b.customer_name}! Your CineVibe booking is ${b.status} for ${b.date} ${b.slot} at ${branch} (${b.guests} guests, ₹${b.amount}). Invoice: ${b.invoice_no || "-"}`;
    const phone = (b.customer_phone || "").replace(/\D/g, "");
    window.open(`https://wa.me/${phone}?text=${encodeURIComponent(msg)}`, "_blank");
  };

  return (
    <div data-testid="bookings-view">
      <div className="flex items-center justify-between mb-4">
        <h2 className="font-display text-2xl text-white">Bookings</h2>
        <Btn onClick={() => setOpen(!open)} data-testid="new-booking-btn"><Plus size={14} className="inline mr-1" />Manual Booking</Btn>
      </div>

      {open && (
        <div className="bg-[#0d0905] border border-[#3a2a14] rounded-lg p-4 mb-5">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
            <Select value={nb.branch_id} onChange={(e) => setNb({ ...nb, branch_id: e.target.value, room_id: "", slot: "" })}>
              <option value="">Branch</option>
              {branches.map((b) => <option key={b.id} value={b.id}>{b.name}</option>)}
            </Select>
            <Select value={nb.room_id} onChange={(e) => setNb({ ...nb, room_id: e.target.value, slot: "" })}>
              <option value="">Room</option>
              {nbRooms.map((r) => <option key={r.id} value={r.id}>{r.name}</option>)}
            </Select>
            <Input value={nb.customer_name} onChange={(e) => setNb({ ...nb, customer_name: e.target.value })} placeholder="Customer name" />
            <Input value={nb.customer_phone} onChange={(e) => setNb({ ...nb, customer_phone: e.target.value })} placeholder="Phone" />
            <Input type="date" value={nb.date} onChange={(e) => setNb({ ...nb, date: e.target.value })} />
            <Select value={nb.slot} onChange={(e) => setNb({ ...nb, slot: e.target.value })}>
              <option value="">Select Slot</option>
              {(() => {
                const selectedRoom = rooms.find((r) => r.id === nb.room_id);
                const selectedBranch = branches.find((b) => b.id === nb.branch_id);
                const roomSlots = Array.isArray(selectedRoom?.slots) ? selectedRoom.slots.filter(Boolean) : [];
                const branchSlots = Array.isArray(selectedBranch?.slots) ? selectedBranch.slots.filter(Boolean) : [];
                const defaultSlots = ["10:00-13:00", "13:30-16:30", "17:00-20:00", "20:30-23:30"];
                const slotList = roomSlots.length ? roomSlots : (branchSlots.length ? branchSlots : defaultSlots);
                return slotList.map((s) => (
                  <option key={s} value={s}>{s.replace("-", " - ")}</option>
                ));
              })()}
            </Select>
            <Input type="number" value={nb.guests} onChange={(e) => setNb({ ...nb, guests: e.target.value })} placeholder="Guests" />
            <Input
              type="number"
              value={nb.amount}
              onChange={(e) => {
                const newAmount = e.target.value;
                const adv = +nb.advance_amount || 0;
                const newBalance = Math.max((+newAmount || 0) - adv, 0);
                setNb({ ...nb, amount: newAmount, balance_amount: newBalance });
              }}
              placeholder="Amount ₹"
            />
            <Input
              type="number"
              min="750"
              value={nb.advance_amount}
              onChange={(e) => {
                const adv = e.target.value;
                const newBalance = Math.max((+nb.amount || 0) - (+adv || 0), 0);
                setNb({ ...nb, advance_amount: adv, balance_amount: newBalance });
              }}
              placeholder="Advance ₹ (min 750)"
              data-testid="advance-amount-input"
            />
            <Input
              type="number"
              value={nb.balance_amount}
              onChange={(e) => setNb({ ...nb, balance_amount: e.target.value })}
              placeholder="Balance ₹"
              data-testid="balance-amount-input"
            />
          </div>
          <div className="text-xs text-[#a89878] mt-2">
            Advance must be at least ₹750. Balance auto-calculates from Amount − Advance, but you can override it.
          </div>
          <div className="mt-3"><Btn onClick={submit} data-testid="save-booking-btn">Save Booking</Btn></div>
        </div>
      )}

      <div className="flex flex-wrap gap-3 mb-4">
        <Select value={filter.city_id} onChange={(e) => setFilter({ ...filter, city_id: e.target.value, branch_id: "" })}>
          <option value="">All Cities</option>
          {cities.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
        </Select>
        <Select value={filter.branch_id} onChange={(e) => setFilter({ ...filter, branch_id: e.target.value })}>
          <option value="">All Branches</option>
          {filteredBranches.map((b) => <option key={b.id} value={b.id}>{b.name}</option>)}
        </Select>
        <Input type="date" value={filter.date} onChange={(e) => setFilter({ ...filter, date: e.target.value })} />
      </div>

      <Table headers={["Date", "Slot", "Branch", "Room", "Customer", "Occasion", "Notes", "Guests", "Amount", "Invoice", "Status", "Actions"]}>
        {list.map((b) => (
          <tr key={b.id} className="border-t border-[#3a2a14]" data-testid={`booking-row-${b.id}`}>
            <td className="py-3 px-3 text-white">{b.date}</td>
            <td className="text-[#bfb393]">{b.slot}</td>
            <td className="text-white">{branchName(b.branch_id)}</td>
            <td className="text-[#bfb393]">{roomName(b.room_id)}</td>
            <td className="text-white">{b.customer_name} <span className="text-xs text-[#a89878]">{b.customer_phone}</span></td>
            <td className="text-[#bfb393] text-xs">{b.occasion ? <span className="px-2 py-0.5 rounded bg-[#2a1a0a] text-[#f4d089]">{b.occasion}</span> : "—"}</td>
            <td className="text-[#bfb393] text-xs max-w-[180px] truncate" title={b.special_request || ""}>{b.special_request || "—"}</td>
            <td className="text-[#bfb393]">{b.guests}</td>
            <td className="text-[#f4d089]">
              <div className="font-semibold">₹{b.amount}</div>
              {(b.payment_status === "paid" || b.payment_status === "mock") ? (
                <div className="text-[10px] leading-tight mt-1 text-[#7ad99c]">
                  Full payment paid
                </div>
              ) : b.payment_type === "advance" ? (
                <div className="text-[10px] leading-tight mt-1">
                  <div className="text-[#7ad99c]">
                    Advance received ₹{b.paid_amount || b.advance_amount || 0}
                  </div>
                  <div className={Number(b.balance_due || 0) > 0 ? "text-red-300" : "text-[#7ad99c]"}>
                    Pending payment ₹{b.balance_due || 0}
                  </div>
                </div>
              ) : (
                <div className="text-[10px] leading-tight mt-1 text-[#7ad99c]">
                  {`Pending payment ₹${b.amount || 0}`}
                </div>
              )}
            </td>
            <td>
              {b.invoice_no ? (
                <a
                  href={`${API}/invoices/${b.id}`}
                  target="_blank" rel="noreferrer"
                  className="text-[#f4d089] underline text-xs"
                  data-testid={`invoice-${b.id}`}
                >{b.invoice_no} ↗</a>
              ) : <span className="text-[#a89878] text-xs">—</span>}
            </td>
            <td>
              <span className={`text-xs px-2 py-1 rounded ${b.status === "completed" ? "bg-[#14323a] text-[#7ad9d9]" : b.status === "cancelled" ? "bg-[#3a1414] text-red-300" : "bg-[#1a3a22] text-[#7ad99c]"}`}>{b.status}</span>
            </td>
            <td className="whitespace-nowrap text-xs pr-3">
              {b.status !== "completed" && b.status !== "cancelled" && (
                <>
                  <button onClick={() => updateStatus(b.id, "completed")} className="text-[#7ad9d9] mr-2" data-testid={`complete-${b.id}`}>Complete</button>
                  <button onClick={() => updateStatus(b.id, "cancelled")} className="text-red-400 mr-2" data-testid={`cancel-${b.id}`}>Cancel</button>
                  {(b.payment_status !== "paid" && b.payment_status !== "mock") && (
                    <button onClick={() => markBalancePaid(b)} className="text-[#f4d089] mr-2" data-testid={`mark-paid-${b.id}`}>Mark Paid</button>
                  )}
                </>
              )}
              {b.customer_phone && (
                <button onClick={() => shareWA(b)} className="text-[#7ad99c]" data-testid={`wa-${b.id}`}>WhatsApp</button>
              )}
            </td>
          </tr>
        ))}
        {list.length === 0 && <tr><td colSpan={12} className="text-center py-8 text-[#a89878]">No bookings yet</td></tr>}
      </Table>
    </div>
  );
}

/* ---------- Branch Managers (Super-admin only) ---------- */
function ManagersView({ branches }) {
  const [list, setList] = useState([]);
  const [f, setF] = useState({ branch_id: "", name: "", email: "", password: "" });
  const [resetFor, setResetFor] = useState(null);
  const [newPw, setNewPw] = useState("");

  const load = async () => {
    const r = await api.get("/admin/branch-admins");
    setList(r.data);
  };
  useEffect(() => { load(); }, []);

  const add = async () => {
    if (!f.branch_id || !f.name.trim() || !f.email.trim() || !f.password) {
      return alert("All fields are required");
    }
    try {
      await api.post("/admin/branch-admins", f);
      setF({ branch_id: "", name: "", email: "", password: "" });
      load();
    } catch (e) {
      alert(e.response?.data?.detail || "Failed to add manager");
    }
  };
  const del = async (id) => {
    if (!window.confirm("Delete this manager?")) return;
    await api.delete(`/admin/branch-admins/${id}`); load();
  };
  const saveReset = async (id) => {
    if (!newPw) return;
    await api.patch(`/admin/branch-admins/${id}`, { password: newPw });
    setResetFor(null); setNewPw("");
    alert("Password updated");
  };

  const branchName = (id) => branches.find((b) => b.id === id)?.name || "—";

  return (
    <div data-testid="managers-view">
      <h2 className="font-display text-2xl text-white mb-1">Branch Managers</h2>
      <p className="text-xs text-[#a89878] mb-5">Assign one manager per branch — they can only see their branch's bookings, rooms and revenue.</p>

      <div className="grid grid-cols-1 md:grid-cols-5 gap-2 mb-5">
        <Select value={f.branch_id} onChange={(e) => setF({ ...f, branch_id: e.target.value })} data-testid="manager-branch">
          <option value="">Select Branch</option>
          {branches.map((b) => <option key={b.id} value={b.id}>{b.name}</option>)}
        </Select>
        <Input value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} placeholder="Full name" data-testid="manager-name" />
        <Input value={f.email} onChange={(e) => setF({ ...f, email: e.target.value })} placeholder="Email (login)" data-testid="manager-email" />
        <Input type="text" value={f.password} onChange={(e) => setF({ ...f, password: e.target.value })} placeholder="Initial password" data-testid="manager-password" />
        <Btn onClick={add} data-testid="add-manager-btn"><Plus size={14} className="inline mr-1" />Assign Manager</Btn>
      </div>

      <Table headers={["Branch", "Name", "Email", "Created", ""]}>
        {list.map((m) => (
          <tr key={m.id} className="border-t border-[#3a2a14]" data-testid={`manager-row-${m.id}`}>
            <td className="py-3 px-3 text-white">{branchName(m.branch_id)}</td>
            <td className="text-white">{m.name}</td>
            <td className="text-[#bfb393]">{m.email}</td>
            <td className="text-[#a89878] text-xs">{(m.created_at || "").slice(0, 10)}</td>
            <td className="text-right pr-3 whitespace-nowrap">
              {resetFor === m.id ? (
                <span className="inline-flex items-center gap-2">
                  <Input type="text" value={newPw} onChange={(e) => setNewPw(e.target.value)} placeholder="New password" className="w-36" data-testid={`new-pw-${m.id}`} />
                  <button onClick={() => saveReset(m.id)} className="text-[#7ad99c] text-xs" data-testid={`save-pw-${m.id}`}>Save</button>
                  <button onClick={() => { setResetFor(null); setNewPw(""); }} className="text-[#a89878] text-xs">Cancel</button>
                </span>
              ) : (
                <button onClick={() => setResetFor(m.id)} className="text-[#f4d089] mr-3 text-xs" data-testid={`reset-pw-${m.id}`}>Reset password</button>
              )}
              <button onClick={() => del(m.id)} className="text-red-400" data-testid={`del-manager-${m.id}`}><Trash2 size={14} /></button>
            </td>
          </tr>
        ))}
        {list.length === 0 && <tr><td colSpan={5} className="text-center py-8 text-[#a89878]">No managers yet — assign one above</td></tr>}
      </Table>
    </div>
  );
}

const Table = ({ headers, children }) => (
  <div className="overflow-x-auto bg-[#0d0905] border border-[#3a2a14] rounded-lg">
    <table className="w-full text-sm">
      <thead>
        <tr>{headers.map((h, i) => <th key={i} className="text-left py-3 px-3 text-[10px] uppercase tracking-wider text-[#a89878]">{h}</th>)}</tr>
      </thead>
      <tbody>{children}</tbody>
    </table>
  </div>
);

/* ---------- Excel Export & Security (Super-admin only) ---------- */
function BackupView() {
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState("");

  const download = async () => {
    setBusy(true); setMsg("");
    try {
      const r = await api.get("/admin/export/excel", { responseType: "blob" });
      const url = URL.createObjectURL(r.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = `cinevibe-export-${new Date().toISOString().slice(0,10)}.xlsx`;
      a.click();
      URL.revokeObjectURL(url);
      setMsg("Excel file downloaded.");
    } catch (e) { setMsg("Export failed: " + (e.response?.data?.detail || e.message)); }
    finally { setBusy(false); }
  };

  return (
    <div data-testid="backup-view">
      <h2 className="font-display text-2xl text-white mb-1">Export</h2>
      <p className="text-xs text-[#a89878] mb-6">Download all data as Excel</p>

      <div className="grid md:grid-cols-2 gap-4">
        <div className="bg-[#0d0905] border border-[#3a2a14] rounded-lg p-5">
          <div className="flex items-center gap-2 text-[#f4d089] font-display text-lg mb-2">
            <Database size={18} /> Export to Excel
          </div>
          <p className="text-xs text-[#a89878] mb-4">
            Downloads a single .xlsx workbook with one sheet each for cities, branches,
            rooms, bookings &amp; managers.
          </p>
          <div className="flex flex-wrap gap-2">
            <Btn onClick={download} disabled={busy} data-testid="export-excel-btn">
              <Download size={14} className="inline mr-1" /> Download Excel (.xlsx)
            </Btn>
          </div>
          {msg && <div className="mt-3 text-xs text-[#bfb393]" data-testid="backup-msg">{msg}</div>}
        </div>
      </div>
    </div>
  );
}

/* ---------- Calendar / Slots ---------- */
function CalendarSlotsView({ branches, rooms }) {
  const [branch_id, setBranch] = useState("");
  const [room_id, setRoom] = useState("");
  const [date, setDate] = useState(new Date().toISOString().slice(0, 10));
  const [slots, setSlots] = useState([]);
  const [slotBookings, setSlotBookings] = useState({});
  const branchRooms = rooms.filter((r) => r.branch_id === branch_id);

  const refresh = async () => {
    if (!room_id || !date) { setSlots([]); setSlotBookings({}); return; }
    const r = await axios.get(`${API}/slots`, { params: { room_id, date } });
    setSlots(r.data);
    // fetch bookings per slot in parallel
    const entries = await Promise.all(r.data.map(async (s) => {
      if (s.status !== "booked") return [s.slot, []];
      const bk = await api.get("/admin/slot-bookings", { params: { room_id, date, slot: s.slot } });
      return [s.slot, bk.data];
    }));
    setSlotBookings(Object.fromEntries(entries));
  };
  useEffect(() => { refresh(); /* eslint-disable-next-line */ }, [room_id, date]);

  const block = async (slot) => {
    const reason = window.prompt("Reason (e.g., maintenance) — optional");
    if (reason === null) return;
    await api.post("/admin/slot-blocks", { room_id, date, slot, reason });
    refresh();
  };
  const unblock = async (slot) => {
    await api.delete(`/admin/slot-blocks`, { params: { room_id, date, slot } });
    refresh();
  };

  return (
    <div data-testid="calendar-view">
      <h2 className="font-display text-2xl text-white mb-1">Calendar / Slots</h2>
      <p className="text-xs text-[#a89878] mb-5">Visualise availability. Click block to hold a slot for maintenance.</p>
      <div className="grid grid-cols-1 md:grid-cols-3 gap-2 mb-5">
        <Select value={branch_id} onChange={(e) => { setBranch(e.target.value); setRoom(""); }} data-testid="cal-branch">
          <option value="">Select Branch</option>
          {branches.map((b) => <option key={b.id} value={b.id}>{b.name}</option>)}
        </Select>
        <Select value={room_id} onChange={(e) => setRoom(e.target.value)} data-testid="cal-room">
          <option value="">Select Room</option>
          {branchRooms.map((r) => <option key={r.id} value={r.id}>{r.name}</option>)}
        </Select>
        <Input type="date" value={date} onChange={(e) => setDate(e.target.value)} data-testid="cal-date" />
      </div>
      {room_id ? (
        slots.length === 0 ? (
          <div className="text-[#a89878] text-sm">No slots configured for this room (or date is in the past). Add slot timings under <span className="text-[#f4d089]">Rooms</span> or <span className="text-[#f4d089]">Branches</span>.</div>
        ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-3">
          {slots.map((found) => {
            const s = found.slot;
            const status = found.status || "available";
            const bookings = slotBookings[s] || [];
            const color = status === "available" ? "border-[#3a2a14] bg-[#0d0905]"
              : status === "booked" ? "border-red-900/40 bg-[#1a0606]"
              : "border-[#a89878]/30 bg-[#1a1410]";
            const label = status === "available" ? "Available" : status === "booked" ? "Booked" : "Blocked";
            const labelColor = status === "available" ? "text-[#7ad99c]" : status === "booked" ? "text-red-400" : "text-[#a89878]";
            return (
              <div key={s} className={`rounded-lg p-4 border ${color}`} data-testid={`slot-${s}`}>
                <div className="flex items-center justify-between">
                  <div className="text-[#f4d089] font-display text-lg">{s}</div>
                  <div className={`text-xs ${labelColor}`}>{label}</div>
                </div>
                {status === "booked" && bookings.length > 0 && (
                  <div className="mt-2 text-xs text-[#bfb393]">
                    {bookings.map((b) => (
                      <div key={b.id}>{b.customer_name} · {b.guests} guests · ₹{b.amount}</div>
                    ))}
                  </div>
                )}
                <div className="mt-3 flex gap-2">
                  {status === "available" && <button onClick={() => block(s)} className="text-xs text-[#a89878] underline" data-testid={`block-${s}`}>Block</button>}
                  {status === "blocked" && <button onClick={() => unblock(s)} className="text-xs text-[#7ad99c] underline" data-testid={`unblock-${s}`}>Unblock</button>}
                </div>
              </div>
            );
          })}
        </div>
        )
      ) : <div className="text-[#a89878] text-sm">Pick a branch and room to view slots.</div>}
    </div>
  );
}

/* ---------- Homepage Content ---------- */
function HomepageView({ token }) {
  const [home, setHome] = useState(null);

  useEffect(() => {
    if (!token) return;

    axios
      .get(`${API}/admin/content/home`, {
        headers: { "X-Admin-Token": token },
      })
      .then((r) => setHome(r.data))
      .catch(() => setHome(null));
  }, [token]);

  const fileToDataUrl = (file) =>
    new Promise((res, rej) => {
      const fr = new FileReader();
      fr.onload = () => res(fr.result);
      fr.onerror = rej;
      fr.readAsDataURL(file);
    });

  if (!home) {
    return (
      <div className="text-[#a89878]">
        Loading homepage content...
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="home-content-panel">

      <h2 className="font-display text-2xl text-[#f4d089]">
        Homepage Content
      </h2>

      {/* Status strip */}
      <div className="rounded-lg border border-[#3a2a14] bg-[#0d0905] p-5 space-y-3">

        <div className="text-[11px] uppercase tracking-[0.18em] text-[#f4d089]">
          Status Strip (4 lines)
        </div>

        {[
          ["strip_slots", "Slots left text"],
          ["strip_viewers", "Viewers text"],
          ["strip_bookings", "Bookings today text"],
          ["strip_rating", "Rating text"],
        ].map(([k, label]) => (
          <div key={k}>
            <div className="text-xs text-[#a89878] mb-1">
              {label}
            </div>

            <input
              value={home[k] || ""}
              onChange={(e) =>
                setHome({
                  ...home,
                  [k]: e.target.value,
                })
              }
              className="w-full bg-[#0a0604] border border-[#3a2a14] rounded-md px-3 py-2 text-sm text-white"
            />
          </div>
        ))}
      </div>

      {/* Food section */}
      <div className="rounded-lg border border-[#3a2a14] bg-[#0d0905] p-5 space-y-4">

        <div className="text-[11px] uppercase tracking-[0.18em] text-[#f4d089]">
          Deliciously Curated Food
        </div>

        <div>
          <div className="text-xs text-[#a89878] mb-1">
            Section Heading
          </div>

          <input
            value={home.food_heading || ""}
            onChange={(e) =>
              setHome({
                ...home,
                food_heading: e.target.value,
              })
            }
            className="w-full bg-[#0a0604] border border-[#3a2a14] rounded-md px-3 py-2 text-sm text-white"
          />
        </div>

        {(home.food_items || []).map((it, idx) => (
          <div
            key={idx}
            className="grid sm:grid-cols-[120px_1fr] gap-3 border border-[#3a2a14] rounded-md p-3"
          >

            <div>
              <img
                src={it.img}
                alt={it.title}
                className="w-[120px] h-[80px] object-cover rounded"
              />

              <label className="block mt-2 text-[11px] text-[#f4d089] cursor-pointer">
                Upload new

                <input
                  type="file"
                  accept="image/*"
                  className="hidden"
                  onChange={async (e) => {
                    const f = e.target.files?.[0];
                    if (!f) return;

                    const data = await fileToDataUrl(f);

                    const items = [...home.food_items];

                    items[idx] = {
                      ...items[idx],
                      img: data,
                    };

                    setHome({
                      ...home,
                      food_items: items,
                    });
                  }}
                />
              </label>
            </div>

            <div className="grid grid-cols-2 gap-2">

              <input
                placeholder="Title"
                value={it.title || ""}
                onChange={(e) => {
                  const items = [...home.food_items];

                  items[idx] = {
                    ...items[idx],
                    title: e.target.value,
                  };

                  setHome({
                    ...home,
                    food_items: items,
                  });
                }}
                className="bg-[#0a0604] border border-[#3a2a14] rounded-md px-3 py-2 text-sm text-white"
              />

              <input
                placeholder="Price"
                value={it.price || ""}
                onChange={(e) => {
                  const items = [...home.food_items];

                  items[idx] = {
                    ...items[idx],
                    price: e.target.value,
                  };

                  setHome({
                    ...home,
                    food_items: items,
                  });
                }}
                className="bg-[#0a0604] border border-[#3a2a14] rounded-md px-3 py-2 text-sm text-white"
              />

              <input
                placeholder="Image URL"
                value={it.img || ""}
                onChange={(e) => {
                  const items = [...home.food_items];

                  items[idx] = {
                    ...items[idx],
                    img: e.target.value,
                  };

                  setHome({
                    ...home,
                    food_items: items,
                  });
                }}
                className="col-span-2 bg-[#0a0604] border border-[#3a2a14] rounded-md px-3 py-2 text-sm text-white"
              />
            </div>
          </div>
        ))}
      </div>

      {/* Franchise Opportunity card */}
      <div className="rounded-lg border border-[#3a2a14] bg-[#0d0905] p-5 space-y-3">
        <div className="text-[11px] uppercase tracking-[0.18em] text-[#f4d089]">
          Franchise Opportunity Card
        </div>
        {[
          ["franchise_heading",       "Heading"],
          ["franchise_subtitle",      "Subtitle"],
          ["franchise_amount",        "Amount (e.g. ₹5-10L)"],
          ["franchise_amount_suffix", "Amount suffix (e.g. Monthly Potential*)"],
          ["franchise_btn_label",     "Button label"],
          ["franchise_btn_url",       "Button URL (e.g. /franchise)"],
        ].map(([k, label]) => (
          <div key={k}>
            <div className="text-xs text-[#a89878] mb-1">{label}</div>
            <input
              value={home[k] || ""}
              onChange={(e) => setHome({ ...home, [k]: e.target.value })}
              className="w-full bg-[#0a0604] border border-[#3a2a14] rounded-md px-3 py-2 text-sm text-white"
              data-testid={`home-${k}`}
            />
          </div>
        ))}
      </div>

      {/* List Your Space card */}
      <div className="rounded-lg border border-[#3a2a14] bg-[#0d0905] p-5 space-y-3">
        <div className="text-[11px] uppercase tracking-[0.18em] text-[#f4d089]">
          List Your Space Card
        </div>
        {[
          ["list_space_heading",   "Heading"],
          ["list_space_title",     "Title (e.g. Earn with CineVibe)"],
          ["list_space_subtitle",  "Subtitle"],
          ["list_space_btn_label", "Button label"],
          ["list_space_btn_url",   "Button URL (e.g. /list-property)"],
        ].map(([k, label]) => (
          <div key={k}>
            <div className="text-xs text-[#a89878] mb-1">{label}</div>
            <input
              value={home[k] || ""}
              onChange={(e) => setHome({ ...home, [k]: e.target.value })}
              className="w-full bg-[#0a0604] border border-[#3a2a14] rounded-md px-3 py-2 text-sm text-white"
              data-testid={`home-${k}`}
            />
          </div>
        ))}
      </div>

      <button
        onClick={async () => {
          await axios.put(
            `${API}/admin/content/home`,
            home,
            {
              headers: {
                "X-Admin-Token": token,
              },
            }
          );

          alert("Homepage content saved");
        }}
        className="bg-gradient-to-b from-[#e8c98a] to-[#c8a266] text-[#1a0f06] font-semibold px-5 py-2.5 rounded-md text-sm"
      >
        Save Homepage
      </button>

    </div>
  );
}                   
                    
/* ---------- Add-ons (CRUD) ---------- */
function AddOnsView() {
  const [list, setList] = useState([]);
  const [f, setF] = useState({ name: "", price: 0, description: "", image: "", status: "active" });
  const [editingId, setEditingId] = useState(null);
  const [edit, setEdit] = useState({ name: "", price: 0, description: "", image: "", status: "active" });
  const load = async () => { const r = await api.get("/admin/addons"); setList(r.data); };
  useEffect(() => { load(); }, []);
  const add = async () => {
    if (!f.name.trim()) return alert("Name required");
    await api.post("/admin/addons", { ...f, price: +f.price });
    setF({ name: "", price: 0, description: "", image: "", status: "active" }); load();
  };
  const toggle = async (a) => {
    await api.patch(`/admin/addons/${a.id}`, { status: a.status === "active" ? "inactive" : "active" });
    load();
  };
  const del = async (id) => { if (!window.confirm("Delete add-on?")) return; await api.delete(`/admin/addons/${id}`); load(); };
  const startEdit = (a) => {
    setEditingId(a.id);
    setEdit({ name: a.name || "", price: a.price ?? 0, description: a.description || "", image: a.image || "", status: a.status || "active" });
  };
  const cancelEdit = () => { setEditingId(null); };
  const saveEdit = async () => {
    if (!edit.name.trim()) return alert("Name required");
    await api.patch(`/admin/addons/${editingId}`, { ...edit, price: +edit.price });
    setEditingId(null); load();
  };
  return (
    <div data-testid="addons-view">
      <h2 className="font-display text-2xl text-white mb-1">Add-ons</h2>
      <p className="text-xs text-[#a89878] mb-5">Decoration, cake, fog, photographer etc. Price is added to booking total (18% GST).</p>
      <div className="grid grid-cols-1 md:grid-cols-5 gap-2 mb-5">
        <Input value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} placeholder="Name" data-testid="addon-name" />
        <Input type="number" value={f.price} onChange={(e) => setF({ ...f, price: e.target.value })} placeholder="Price ₹" data-testid="addon-price" />
        <Input value={f.description} onChange={(e) => setF({ ...f, description: e.target.value })} placeholder="Description" />
        <Input value={f.image} onChange={(e) => setF({ ...f, image: e.target.value })} placeholder="Image URL (optional)" />
        <Btn onClick={add} data-testid="add-addon-btn"><Plus size={14} className="inline mr-1" />Add</Btn>
      </div>
      <Table headers={["Name", "Price", "Description", "Image", "Status", ""]}>
        {list.map((a) => (
          editingId === a.id ? (
            <tr key={a.id} className="border-t border-[#3a2a14] bg-[#0d0905]" data-testid={`addon-edit-row-${a.id}`}>
              <td className="py-2 px-3"><Input value={edit.name} onChange={(e) => setEdit({ ...edit, name: e.target.value })} data-testid={`edit-addon-name-${a.id}`} /></td>
              <td><Input type="number" value={edit.price} onChange={(e) => setEdit({ ...edit, price: e.target.value })} data-testid={`edit-addon-price-${a.id}`} /></td>
              <td><Input value={edit.description} onChange={(e) => setEdit({ ...edit, description: e.target.value })} /></td>
              <td><Input value={edit.image} onChange={(e) => setEdit({ ...edit, image: e.target.value })} placeholder="Image URL" /></td>
              <td>
                <select value={edit.status} onChange={(e) => setEdit({ ...edit, status: e.target.value })} className="bg-[#1a0f06] border border-[#3a2a14] text-white text-xs px-2 py-1 rounded">
                  <option value="active">Active</option>
                  <option value="inactive">Inactive</option>
                </select>
              </td>
              <td className="text-right pr-3 whitespace-nowrap">
                <button onClick={saveEdit} className="text-[#7ad99c] text-xs px-2 py-1 mr-1" data-testid={`save-addon-${a.id}`}>Save</button>
                <button onClick={cancelEdit} className="text-[#a89878] text-xs px-2 py-1">Cancel</button>
              </td>
            </tr>
          ) : (
            <tr key={a.id} className="border-t border-[#3a2a14]" data-testid={`addon-row-${a.id}`}>
              <td className="py-3 px-3 text-white">{a.name}</td>
              <td className="text-[#f4d089]">₹{a.price}</td>
              <td className="text-[#bfb393] text-xs">{a.description || "—"}</td>
              <td>{a.image ? <img src={a.image} alt={a.name} className="w-10 h-10 rounded object-cover" /> : <span className="text-[#a89878] text-xs">—</span>}</td>
              <td>
                <button onClick={() => toggle(a)} className={`text-xs px-2 py-1 rounded ${a.status === "active" ? "bg-[#1a3a22] text-[#7ad99c]" : "bg-[#3a1414] text-red-300"}`} data-testid={`toggle-addon-${a.id}`}>
                  {a.status === "active" ? "Active" : "Inactive"}
                </button>
              </td>
              <td className="text-right pr-3 whitespace-nowrap">
                <button onClick={() => startEdit(a)} className="text-[#f4d089] text-xs px-2 py-1 mr-1" data-testid={`edit-addon-${a.id}`}>Edit</button>
                <button onClick={() => del(a.id)} className="text-red-400" data-testid={`del-addon-${a.id}`}><Trash2 size={14} /></button>
              </td>
            </tr>
          )
        ))}
        {list.length === 0 && <tr><td colSpan={6} className="text-center py-8 text-[#a89878]">No add-ons yet</td></tr>}
      </Table>
    </div>
  );
}

/* ---------- Coupons (CRUD) ---------- */
function CouponsView() {
  const [list, setList] = useState([]);
  const [f, setF] = useState({ code: "", discount_percent: 5, description: "", min_amount: 0, max_discount: 0, status: "active" });
  const [editingId, setEditingId] = useState(null);
  const [edit, setEdit] = useState({ code: "", discount_percent: 0, description: "", min_amount: 0, max_discount: 0, status: "active" });

  const load = async () => { const r = await api.get("/admin/coupons"); setList(r.data); };
  useEffect(() => { load(); }, []);

  const add = async () => {
    if (!f.code.trim()) return alert("Code required");
    if (!f.discount_percent || f.discount_percent <= 0) return alert("Discount % must be > 0");
    try {
      await api.post("/admin/coupons", {
        ...f,
        code: f.code.trim().toUpperCase(),
        discount_percent: +f.discount_percent,
        min_amount: +f.min_amount,
        max_discount: +f.max_discount,
      });
      setF({ code: "", discount_percent: 5, description: "", min_amount: 0, max_discount: 0, status: "active" });
      load();
    } catch (e) {
      alert(e.response?.data?.detail || "Failed to add coupon");
    }
  };

  const toggle = async (c) => {
    await api.patch(`/admin/coupons/${c.id}`, { status: c.status === "active" ? "inactive" : "active" });
    load();
  };

  const del = async (id) => {
    if (!window.confirm("Delete coupon?")) return;
    await api.delete(`/admin/coupons/${id}`);
    load();
  };

  const startEdit = (c) => {
    setEditingId(c.id);
    setEdit({
      code: c.code || "",
      discount_percent: c.discount_percent ?? 0,
      description: c.description || "",
      min_amount: c.min_amount ?? 0,
      max_discount: c.max_discount ?? 0,
      status: c.status || "active",
    });
  };
  const cancelEdit = () => setEditingId(null);
  const saveEdit = async () => {
    if (!edit.code.trim()) return alert("Code required");
    try {
      await api.patch(`/admin/coupons/${editingId}`, {
        ...edit,
        code: edit.code.trim().toUpperCase(),
        discount_percent: +edit.discount_percent,
        min_amount: +edit.min_amount,
        max_discount: +edit.max_discount,
      });
      setEditingId(null);
      load();
    } catch (e) {
      alert(e.response?.data?.detail || "Save failed");
    }
  };

  return (
    <div data-testid="coupons-view">
      <h2 className="font-display text-2xl text-white mb-1">Coupons</h2>
      <p className="text-xs text-[#a89878] mb-5">Promo codes with % discount. Optional min bill amount and max discount cap.</p>

      <div className="grid grid-cols-1 md:grid-cols-6 gap-2 mb-5">
  <Input value={f.code} onChange={(e) => setF({ ...f, code: e.target.value.toUpperCase() })} placeholder="CODE (e.g. WELCOME10)" data-testid="coupon-code" />
  <Input type="number" value={f.discount_percent} onChange={(e) => setF({ ...f, discount_percent: e.target.value })} placeholder="Discount %" data-testid="coupon-pct" />
  <Input type="number" value={f.min_amount} onChange={(e) => setF({ ...f, min_amount: e.target.value })} placeholder="Min bill ₹ (0 = none)" />
  <Input type="number" value={f.max_discount} onChange={(e) => setF({ ...f, max_discount: e.target.value })} placeholder="Max discount ₹ (0 = none)" />
  <Input value={f.description} onChange={(e) => setF({ ...f, description: e.target.value })} placeholder="Description (optional)" />
  <Btn onClick={add} data-testid="add-coupon-btn"><Plus size={14} className="inline mr-1" />Add Coupon</Btn>
</div>

      <Table headers={["Code", "Discount %", "Min Bill", "Max ₹", "Description", "Status", ""]}>
        {list.map((c) => (
          editingId === c.id ? (
            <tr key={c.id} className="border-t border-[#3a2a14] bg-[#0d0905]">
              <td className="py-2 px-3"><Input value={edit.code} onChange={(e) => setEdit({ ...edit, code: e.target.value.toUpperCase() })} /></td>
              <td><Input type="number" value={edit.discount_percent} onChange={(e) => setEdit({ ...edit, discount_percent: e.target.value })} /></td>
              <td><Input type="number" value={edit.min_amount} onChange={(e) => setEdit({ ...edit, min_amount: e.target.value })} /></td>
              <td><Input type="number" value={edit.max_discount} onChange={(e) => setEdit({ ...edit, max_discount: e.target.value })} /></td>
              <td><Input value={edit.description} onChange={(e) => setEdit({ ...edit, description: e.target.value })} /></td>
              <td>
                <select value={edit.status} onChange={(e) => setEdit({ ...edit, status: e.target.value })} className="bg-[#1a0f06] border border-[#3a2a14] text-white text-xs px-2 py-1 rounded">
                  <option value="active">Active</option>
                  <option value="inactive">Inactive</option>
                </select>
              </td>
              <td className="text-right pr-3 whitespace-nowrap">
                <button onClick={saveEdit} className="text-[#7ad99c] text-xs px-2 mr-1" data-testid={`save-coupon-${c.id}`}>Save</button>
                <button onClick={cancelEdit} className="text-[#a89878] text-xs px-2">Cancel</button>
              </td>
            </tr>
          ) : (
            <tr key={c.id} className="border-t border-[#3a2a14]" data-testid={`coupon-row-${c.id}`}>
              <td className="py-3 px-3 text-[#f4d089] font-semibold">{c.code}</td>
              <td className="text-white">{c.discount_percent}%</td>
              <td className="text-[#bfb393]">{c.min_amount ? `₹${c.min_amount}` : "—"}</td>
              <td className="text-[#bfb393]">{c.max_discount ? `₹${c.max_discount}` : "—"}</td>
              <td className="text-[#bfb393] text-xs">{c.description || "—"}</td>
              <td>
                <button onClick={() => toggle(c)} className={`text-xs px-2 py-1 rounded ${c.status === "active" ? "bg-[#1a3a22] text-[#7ad99c]" : "bg-[#3a1414] text-red-300"}`} data-testid={`toggle-coupon-${c.id}`}>
                  {c.status === "active" ? "Active" : "Inactive"}
                </button>
              </td>
              <td className="text-right pr-3 whitespace-nowrap">
                <button onClick={() => startEdit(c)} className="text-[#f4d089] mr-3 text-xs" data-testid={`edit-coupon-${c.id}`}>Edit</button>
                <button onClick={() => del(c.id)} className="text-red-400" data-testid={`del-coupon-${c.id}`}><Trash2 size={14} /></button>
              </td>
            </tr>
          )
        ))}
        {list.length === 0 && <tr><td colSpan={7} className="text-center py-8 text-[#a89878]">No coupons yet — add one above</td></tr>}
      </Table>
    </div>
  );
}

/* ---------- Experiences (CRUD) ---------- */
function ExperiencesView() {
  const [list, setList] = useState([]);
  const [addons, setAddons] = useState([]);
  const empty = { name: "", slug: "", sub: "", description: "", image: "", hrs: "3 HRS", ppl: "2-6 People", badge: "", base_price: 0, add_ons: [], status: "active" };
  const [f, setF] = useState(empty);
  const [editingId, setEditingId] = useState(null);

  const load = async () => {
    const [r, a] = await Promise.all([api.get("/admin/experiences"), api.get("/admin/addons")]);
    setList(r.data); setAddons(a.data);
  };
  useEffect(() => { load(); }, []);

  const toggleAddon = (id) =>
    setF((x) => ({ ...x, add_ons: x.add_ons.includes(id) ? x.add_ons.filter((y) => y !== id) : [...x.add_ons, id] }));

  const startEdit = (x) => {
    setEditingId(x.id);
    setF({ ...empty, ...x, base_price: x.base_price || 0, add_ons: x.add_ons || [] });
    window.scrollTo({ top: 0, behavior: "smooth" });
  };
  const cancelEdit = () => { setEditingId(null); setF(empty); };

  const save = async () => {
    if (!f.name.trim()) return alert("Name required");
    const payload = { ...f, base_price: +f.base_price };
    if (editingId) {
      await api.patch(`/admin/experiences/${editingId}`, payload);
    } else {
      await api.post("/admin/experiences", payload);
    }
    cancelEdit(); load();
  };

  const toggle = async (x) => {
    await api.patch(`/admin/experiences/${x.id}`, { status: x.status === "active" ? "inactive" : "active" });
    load();
  };
  const del = async (id) => { if (!window.confirm("Delete experience?")) return; await api.delete(`/admin/experiences/${id}`); load(); };

  return (
    <div data-testid="experiences-view">
      <h2 className="font-display text-2xl text-white mb-1">Experiences</h2>
      <p className="text-xs text-[#a89878] mb-5">Curated packages shown on Home & /experiences. Drives the “Your Experience” section.</p>

      <div className="bg-[#0d0905] border border-[#3a2a14] rounded-lg p-4 mb-5">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-2 mb-2">
          <Input value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} placeholder="Name (e.g. Birthday Celebration)" data-testid="exp-name" />
          <Input value={f.slug} onChange={(e) => setF({ ...f, slug: e.target.value })} placeholder="Slug (auto if empty, e.g. birthday-celebration)" data-testid="exp-slug" />
          <Input value={f.sub} onChange={(e) => setF({ ...f, sub: e.target.value })} placeholder="Subtitle (e.g. Make birthdays magical)" />
          <Input value={f.badge} onChange={(e) => setF({ ...f, badge: e.target.value })} placeholder="Badge (Most Booked / Trending / Premium / blank)" />
          <Input value={f.image} onChange={(e) => setF({ ...f, image: e.target.value })} placeholder="Image URL" className="md:col-span-2" />
          <Input value={f.description} onChange={(e) => setF({ ...f, description: e.target.value })} placeholder="Long description (shown on detail page)" className="md:col-span-3" />
        </div>
        {addons.length > 0 && (
          <div className="flex flex-wrap gap-2 mb-3">
            <span className="text-xs text-[#a89878]">Default add-ons:</span>
            {addons.map((a) => (
              <button key={a.id} onClick={() => toggleAddon(a.id)} className={`text-xs px-2 py-1 rounded border ${f.add_ons.includes(a.id) ? "border-[#d4a574] text-[#f4d089] bg-[#1a0f06]" : "border-[#3a2a14] text-[#bfb393]"}`} data-testid={`exp-addon-${a.id}`}>
                {a.name}
              </button>
            ))}
          </div>
        )}
        <div className="flex gap-2 items-center">
          <Btn onClick={save} data-testid="add-exp-btn">
            <Plus size={14} className="inline mr-1" />{editingId ? "Save Changes" : "Add Experience"}
          </Btn>
          {editingId && (
            <button onClick={cancelEdit} className="text-xs text-[#a89878] underline">Cancel</button>
          )}
        </div>
      </div>

      <Table headers={["Image", "Name", "Slug", "Badge", "Status", ""]}>
        {list.map((x) => (
          <tr key={x.id} className="border-t border-[#3a2a14]" data-testid={`exp-row-${x.id}`}>
            <td className="py-3 px-3">
              {x.image ? <img src={x.image} alt={x.name} className="w-14 h-10 object-cover rounded" /> : <span className="text-[#a89878] text-xs">—</span>}
            </td>
            <td className="text-white">{x.name}<div className="text-[11px] text-[#a89878]">{x.sub || "—"}</div></td>
            <td className="text-[#bfb393] text-xs">{x.slug || "—"}</td>
            <td className="text-[#bfb393] text-xs">{x.badge || "—"}</td>
            <td>
              <button onClick={() => toggle(x)} className={`text-xs px-2 py-1 rounded ${x.status === "active" ? "bg-[#1a3a22] text-[#7ad99c]" : "bg-[#3a1414] text-red-300"}`} data-testid={`toggle-exp-${x.id}`}>
                {x.status === "active" ? "Active" : "Inactive"}
              </button>
            </td>
            <td className="text-right pr-3 whitespace-nowrap">
              <button onClick={() => startEdit(x)} className="text-[#f4d089] mr-3 text-xs" data-testid={`edit-exp-${x.id}`}>Edit</button>
              <button onClick={() => del(x.id)} className="text-red-400" data-testid={`del-exp-${x.id}`}><Trash2 size={14} /></button>
            </td>
          </tr>
        ))}
        {list.length === 0 && <tr><td colSpan={6} className="text-center py-8 text-[#a89878]">No experiences yet</td></tr>}
      </Table>
    </div>
  );
}



/* ---------- Packages (CRUD) ---------- */
function PackagesView() {
  const [list, setList] = useState([]);
  const empty = { name: "", slug: "", sub: "", description: "", image: "", badge: "", base_price: 0, status: "active" };
  const [f, setF] = useState(empty);
  const [editingId, setEditingId] = useState(null);

  const load = async () => {
    const r = await api.get("/admin/packages");
    setList(r.data);
  };
  useEffect(() => { load(); }, []);

  const startEdit = (x) => {
    setEditingId(x.id);
    setF({ ...empty, ...x, base_price: x.base_price || 0 });
    window.scrollTo({ top: 0, behavior: "smooth" });
  };
  const cancelEdit = () => { setEditingId(null); setF(empty); };

  const save = async () => {
    if (!f.name.trim()) return alert("Name required");
    const payload = { ...f, base_price: +f.base_price };
    if (editingId) {
      await api.patch(`/admin/packages/${editingId}`, payload);
    } else {
      await api.post("/admin/packages", payload);
    }
    cancelEdit(); load();
  };

  const toggle = async (x) => {
    await api.patch(`/admin/packages/${x.id}`, { status: x.status === "active" ? "inactive" : "active" });
    load();
  };
  const del = async (id) => { if (!window.confirm("Delete package?")) return; await api.delete(`/admin/packages/${id}`); load(); };

  return (
    <div data-testid="packages-view">
      <h2 className="font-display text-2xl text-white mb-1">Packages</h2>
      <p className="text-xs text-[#a89878] mb-5">Ready packages shown on Home & /packages.</p>

      <div className="bg-[#0d0905] border border-[#3a2a14] rounded-lg p-4 mb-5">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-2 mb-2">
          <Input value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} placeholder="Name (e.g. Birthday Package)" data-testid="pkg-name" />
          <Input value={f.slug} onChange={(e) => setF({ ...f, slug: e.target.value })} placeholder="Slug (auto if empty)" data-testid="pkg-slug" />
          <Input type="number" value={f.base_price} onChange={(e) => setF({ ...f, base_price: e.target.value })} placeholder="Starting price ₹" data-testid="pkg-price" />
          <Input value={f.sub} onChange={(e) => setF({ ...f, sub: e.target.value })} placeholder="Subtitle (e.g. Balloon decor, your cake, your movie)" />
          <Input value={f.badge} onChange={(e) => setF({ ...f, badge: e.target.value })} placeholder="Badge (Most Booked / Exclusive / blank)" />
          <Input value={f.image} onChange={(e) => setF({ ...f, image: e.target.value })} placeholder="Image URL" className="md:col-span-2" />
          <Input value={f.description} onChange={(e) => setF({ ...f, description: e.target.value })} placeholder="Long description (shown on detail page)" className="md:col-span-3" />
        </div>
        <div className="flex gap-2 items-center">
          <Btn onClick={save} data-testid="add-pkg-btn">
            <Plus size={14} className="inline mr-1" />{editingId ? "Save Changes" : "Add Package"}
          </Btn>
          {editingId && (
            <button onClick={cancelEdit} className="text-xs text-[#a89878] underline">Cancel</button>
          )}
        </div>
      </div>

      <Table headers={["Image", "Name", "Slug", "Price", "Badge", "Status", ""]}>
        {list.map((x) => (
          <tr key={x.id} className="border-t border-[#3a2a14]" data-testid={`pkg-row-${x.id}`}>
            <td className="py-3 px-3">
              {x.image ? <img src={x.image} alt={x.name} className="w-14 h-10 object-cover rounded" /> : <span className="text-[#a89878] text-xs">—</span>}
            </td>
            <td className="text-white">{x.name}<div className="text-[11px] text-[#a89878]">{x.sub || "—"}</div></td>
            <td className="text-[#bfb393] text-xs">{x.slug || "—"}</td>
            <td className="text-[#f4d089]">₹{x.base_price}</td>
            <td className="text-[#bfb393] text-xs">{x.badge || "—"}</td>
            <td>
              <button onClick={() => toggle(x)} className={`text-xs px-2 py-1 rounded ${x.status === "active" ? "bg-[#1a3a22] text-[#7ad99c]" : "bg-[#3a1414] text-red-300"}`} data-testid={`toggle-pkg-${x.id}`}>
                {x.status === "active" ? "Active" : "Inactive"}
              </button>
            </td>
            <td className="text-right pr-3 whitespace-nowrap">
              <button onClick={() => startEdit(x)} className="text-[#f4d089] mr-3 text-xs" data-testid={`edit-pkg-${x.id}`}>Edit</button>
              <button onClick={() => del(x.id)} className="text-red-400" data-testid={`del-pkg-${x.id}`}><Trash2 size={14} /></button>
            </td>
          </tr>
        ))}
        {list.length === 0 && <tr><td colSpan={7} className="text-center py-8 text-[#a89878]">No packages yet</td></tr>}
      </Table>
    </div>
  );
}
/* ---------- Food (Billing Software) ---------- */
function FoodPetpoojaView({ cities, branches }) {
  const cityName = (id) => cities.find((c) => c.id === id)?.name || "—";
  return (
    <div data-testid="food-view">
      <h2 className="font-display text-2xl text-white mb-1">Food (Billing Software)</h2>
      <p className="text-xs text-[#a89878] mb-5">Per-branch Billing Software menu URLs. Food is billed by Billing Software (GST 5%) — not on CineVibe invoices.</p>
      <Table headers={["City", "Branch", "Menu URL", "Café Phone", "QR"]}>
        {branches.map((b) => (
          <tr key={b.id} className="border-t border-[#3a2a14]">
            <td className="py-3 px-3 text-white">{cityName(b.city_id)}</td>
            <td className="text-white">{b.name}</td>
            <td className="text-[#bfb393] text-xs max-w-[320px]">
              {b.petpooja_menu_url
                ? <a className="text-[#7ad99c] underline break-all" target="_blank" rel="noreferrer" href={b.petpooja_menu_url}>{b.petpooja_menu_url}</a>
                : <span className="italic text-[#a89878]">— not set —</span>}
            </td>
            <td className="text-[#bfb393]">{b.food_phone || "—"}</td>
            <td>
              {b.petpooja_menu_url
                ? <a target="_blank" rel="noreferrer" href={`https://api.qrserver.com/v1/create-qr-code/?size=120x120&data=${encodeURIComponent(b.petpooja_menu_url)}`} className="text-[#f4d089] underline text-xs">View QR ↗</a>
                : <span className="text-[#a89878] text-xs">—</span>}
            </td>
          </tr>
        ))}
        {branches.length === 0 && <tr><td colSpan={5} className="text-center py-8 text-[#a89878]">No branches yet</td></tr>}
      </Table>
    </div>
  );
}

/* ---------- Billing & Invoices ---------- */
function BillingInvoicesView({ branches }) {
  const [list, setList] = useState([]);
  const [filter, setFilter] = useState({ branch_id: "", date: "" });
  const branchName = (id) => branches.find((b) => b.id === id)?.name || "—";
  const load = async () => {
    const params = Object.fromEntries(Object.entries(filter).filter(([_, v]) => v));
    const r = await api.get("/admin/bookings", { params });
    setList(r.data);
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [filter]);
  const totalGst = list.reduce((s, b) => s + (b.gst_amount || 0), 0);
  const totalAmt = list.reduce((s, b) => s + (b.amount || 0), 0);
  return (
    <div data-testid="billing-view">
      <h2 className="font-display text-2xl text-white mb-1">Billing & Invoices</h2>
      <p className="text-xs text-[#a89878] mb-5">GST 18% screening invoices. Click an invoice number to download the PDF.</p>
      <div className="flex flex-wrap gap-3 mb-4">
        <Select value={filter.branch_id} onChange={(e) => setFilter({ ...filter, branch_id: e.target.value })}>
          <option value="">All Branches</option>
          {branches.map((b) => <option key={b.id} value={b.id}>{b.name}</option>)}
        </Select>
        <Input type="date" value={filter.date} onChange={(e) => setFilter({ ...filter, date: e.target.value })} />
      </div>
      <div className="grid grid-cols-2 md:grid-cols-3 gap-3 mb-5">
        <StatCard label="Invoices" value={list.length} testid="bill-count" />
        <StatCard label="Total (incl. GST)" value={`₹${totalAmt.toLocaleString()}`} testid="bill-total" />
        <StatCard label="GST Collected" value={`₹${totalGst.toLocaleString()}`} testid="bill-gst" />
      </div>
      <Table headers={["Invoice", "Date", "Branch", "Customer", "Amount", "GST", "PDF", "Share"]}>
        {list.map((b) => {
          const share = () => {
            const url = `${API}/invoices/${b.id}`;
            const phone = (b.customer_phone || "").replace(/\D/g, "");
            const msg = `Hi ${b.customer_name}, your CineVibe invoice ${b.invoice_no || ""} for ₹${b.amount}: ${url}`;
            window.open(`https://wa.me/${phone}?text=${encodeURIComponent(msg)}`, "_blank");
          };
          return (
            <tr key={b.id} className="border-t border-[#3a2a14]">
              <td className="py-3 px-3 text-[#f4d089]">{b.invoice_no || "—"}</td>
              <td className="text-[#bfb393]">{b.date}</td>
              <td className="text-white">{branchName(b.branch_id)}</td>
              <td className="text-white">{b.customer_name}</td>
              <td className="text-[#f4d089]">₹{b.amount}</td>
              <td className="text-[#bfb393]">₹{b.gst_amount || 0}</td>
              <td>
                {b.invoice_no
                  ? <a href={`${API}/invoices/${b.id}`} target="_blank" rel="noreferrer" className="text-[#f4d089] underline text-xs">Download ↗</a>
                  : <span className="text-[#a89878] text-xs">—</span>}
              </td>
              <td>
                {b.invoice_no && b.customer_phone
                  ? <button onClick={share} className="text-[#7ad99c] underline text-xs" data-testid={`bill-wa-${b.id}`}>WhatsApp</button>
                  : <span className="text-[#a89878] text-xs">—</span>}
              </td>
            </tr>
          );
        })}
        {list.length === 0 && <tr><td colSpan={8} className="text-center py-8 text-[#a89878]">No invoices yet</td></tr>}
      </Table>
    </div>
  );
}

/* ---------- Payments ---------- */
function PaymentsView({ branches }) {
  const [list, setList] = useState([]);
  const [cfg, setCfg] = useState(null);
  const [filter, setFilter] = useState({ branch_id: "", status: "" });
  const branchName = (id) => branches.find((b) => b.id === id)?.name || "—";
  const load = async () => {
    const params = filter.branch_id ? { branch_id: filter.branch_id } : {};
    const r = await api.get("/admin/bookings", { params });
    let rows = r.data;
    if (filter.status) rows = rows.filter((b) => (b.payment_status || "unpaid") === filter.status);
    setList(rows);
  };
  useEffect(() => { axios.get(`${API}/payments/config`).then((r) => setCfg(r.data)); }, []);
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [filter]);
  const counts = list.reduce((a, b) => { const k = b.payment_status || "unpaid"; a[k] = (a[k] || 0) + 1; return a; }, {});
  return (
    <div data-testid="payments-view">
      <h2 className="font-display text-2xl text-white mb-1">Payments</h2>
      <p className="text-xs text-[#a89878] mb-5">
        Razorpay status:{" "}
        <span className={cfg?.enabled ? "text-[#7ad99c]" : "text-[#f4d089]"}>
          {cfg ? (cfg.enabled ? "LIVE (HMAC-verified)" : "PLACEHOLDER (mock mode)") : "…"}
        </span>
      </p>
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-5">
        <StatCard label="Paid" value={counts.paid || 0} testid="pay-paid" />
        <StatCard label="Mock" value={counts.mock || 0} testid="pay-mock" />
        <StatCard label="Unpaid" value={counts.unpaid || 0} testid="pay-unpaid" />
        <StatCard label="Total" value={list.length} testid="pay-total" />
      </div>
      <div className="flex flex-wrap gap-3 mb-4">
        <Select value={filter.branch_id} onChange={(e) => setFilter({ ...filter, branch_id: e.target.value })}>
          <option value="">All Branches</option>
          {branches.map((b) => <option key={b.id} value={b.id}>{b.name}</option>)}
        </Select>
        <Select value={filter.status} onChange={(e) => setFilter({ ...filter, status: e.target.value })}>
          <option value="">All Statuses</option>
          <option value="paid">Paid</option>
          <option value="mock">Mock</option>
          <option value="unpaid">Unpaid</option>
        </Select>
      </div>
      <Table headers={["Date", "Branch", "Customer", "Amount", "Status", "Razorpay Order", "Payment ID"]}>
        {list.map((b) => (
          <tr key={b.id} className="border-t border-[#3a2a14]">
            <td className="py-3 px-3 text-white">{b.date}</td>
            <td className="text-white">{branchName(b.branch_id)}</td>
            <td className="text-white">{b.customer_name}</td>
            <td className="text-[#f4d089]">₹{b.amount}</td>
            <td><span className={`text-xs px-2 py-1 rounded ${b.payment_status === "paid" ? "bg-[#1a3a22] text-[#7ad99c]" : b.payment_status === "mock" ? "bg-[#3a2a14] text-[#f4d089]" : "bg-[#3a1414] text-red-300"}`}>{b.payment_status || "unpaid"}</span></td>
            <td className="text-[#bfb393] text-xs">{b.razorpay_order_id || "—"}</td>
            <td className="text-[#bfb393] text-xs">{b.payment_id || "—"}</td>
          </tr>
        ))}
        {list.length === 0 && <tr><td colSpan={7} className="text-center py-8 text-[#a89878]">No payments to show</td></tr>}
      </Table>
    </div>
  );
}

/* ---------- Reports ---------- */
function ReportsView({ cities, branches }) {
  const [tab, setTab] = useState("bookings");
  const [from, setFrom] = useState("");
  const [to, setTo] = useState("");
  const [cityId, setCityId] = useState("");
  const [branchIds, setBranchIds] = useState([]);
  const [status, setStatus] = useState("");
  const [payStatus, setPayStatus] = useState("");
  const [source, setSource] = useState("");
  const [group, setGroup] = useState("daily");
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(false);

  const visibleBranches = cityId ? branches.filter(b => b.city_id === cityId) : branches;

  const buildParams = () => {
    const p = new URLSearchParams();
    if (from) p.set("from_date", from);
    if (to) p.set("to_date", to);
    if (branchIds.length) p.set("branch_ids", branchIds.join(","));
    else if (cityId) p.set("city_id", cityId);
    if (tab === "bookings") {
      if (status) p.set("status", status);
      if (payStatus) p.set("payment_status", payStatus);
      if (source) p.set("source", source);
    }
    if (tab === "revenue") p.set("group", group);
    return p;
  };

  const load = async () => {
    setBusy(true);
    try {
      const p = buildParams(); p.set("format", "json");
      const r = await api.get(`/admin/reports/${tab}?${p.toString()}`);
      setData(r.data);
    } catch (e) { setData({ error: e.response?.data?.detail || e.message }); }
    finally { setBusy(false); }
  };

  const download = async (fmt) => {
    const p = buildParams(); p.set("format", fmt);
    const r = await api.get(`/admin/reports/${tab}?${p.toString()}`, { responseType: "blob" });
    const url = URL.createObjectURL(r.data);
    const a = document.createElement("a");
    a.href = url; a.download = `${tab}_${new Date().toISOString().slice(0,10)}.${fmt}`;
    a.click(); URL.revokeObjectURL(url);
  };

  useEffect(() => { load(); /* eslint-disable-line */ }, [tab]);

  const tabs = [
    ["bookings","Bookings"],["revenue","Revenue"],["addons","Add-on Sales"],
    ["occupancy","Occupancy"],["customers","Customers"],
    ["gst","GST"],["payments","Payments"],["cancellations","Cancellations"],
  ];

  const rows = data?.rows || [];
  const headers = data?.headers || [];

  return (
    <div data-testid="reports-view">
      <h2 className="font-display text-2xl text-white mb-1">Reports</h2>
      <p className="text-xs text-[#a89878] mb-4">Filter, preview and download as CSV, Excel or PDF.</p>

      <div className="flex flex-wrap gap-2 mb-4">
        {tabs.map(([k,l]) => (
          <button key={k} onClick={() => setTab(k)}
            data-testid={`report-tab-${k}`}
            className={`px-3 py-1.5 rounded text-xs ${tab===k?'bg-[#f4d089] text-black':'bg-[#1a0f06] text-[#bfb393] border border-[#3a2a14]'}`}>
            {l}
          </button>
        ))}
      </div>

      <div className="grid md:grid-cols-3 lg:grid-cols-6 gap-3 mb-4 bg-[#0d0905] p-4 rounded border border-[#3a2a14]">
        <label className="text-xs text-[#a89878]">From
          <input type="date" value={from} onChange={e=>setFrom(e.target.value)} className="w-full bg-[#1a0f06] text-white p-2 rounded border border-[#3a2a14] text-xs mt-1" data-testid="rpt-from"/>
        </label>
        <label className="text-xs text-[#a89878]">To
          <input type="date" value={to} onChange={e=>setTo(e.target.value)} className="w-full bg-[#1a0f06] text-white p-2 rounded border border-[#3a2a14] text-xs mt-1" data-testid="rpt-to"/>
        </label>
        <label className="text-xs text-[#a89878]">City
          <select value={cityId} onChange={e=>{setCityId(e.target.value); setBranchIds([]);}} className="w-full bg-[#1a0f06] text-white p-2 rounded border border-[#3a2a14] text-xs mt-1" data-testid="rpt-city">
            <option value="">All cities</option>
            {cities.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
          </select>
        </label>
        <label className="text-xs text-[#a89878]">Branches (Ctrl/Cmd to select multiple)
          <select multiple value={branchIds} onChange={e=>setBranchIds(Array.from(e.target.selectedOptions).map(o=>o.value))} className="w-full bg-[#1a0f06] text-white p-2 rounded border border-[#3a2a14] text-xs mt-1 h-20" data-testid="rpt-branches">
            {visibleBranches.map(b => <option key={b.id} value={b.id}>{b.name}</option>)}
          </select>
        </label>
        {tab==="bookings" && (<>
          <label className="text-xs text-[#a89878]">Status
            <select value={status} onChange={e=>setStatus(e.target.value)} className="w-full bg-[#1a0f06] text-white p-2 rounded border border-[#3a2a14] text-xs mt-1">
              <option value="">Any</option><option value="confirmed">Confirmed</option>
              <option value="completed">Completed</option><option value="cancelled">Cancelled</option>
            </select>
          </label>
          <label className="text-xs text-[#a89878]">Payment
            <select value={payStatus} onChange={e=>setPayStatus(e.target.value)} className="w-full bg-[#1a0f06] text-white p-2 rounded border border-[#3a2a14] text-xs mt-1">
              <option value="">Any</option><option value="paid">Paid</option>
              <option value="partial">Partial</option><option value="unpaid">Unpaid</option>
            </select>
          </label>
          <label className="text-xs text-[#a89878]">Source
            <select value={source} onChange={e=>setSource(e.target.value)} className="w-full bg-[#1a0f06] text-white p-2 rounded border border-[#3a2a14] text-xs mt-1">
              <option value="">Any</option><option value="website">Website</option>
              <option value="whatsapp">WhatsApp</option><option value="manual">Manual</option>
            </select>
          </label>
        </>)}
        {tab==="revenue" && (
          <label className="text-xs text-[#a89878]">Group by
            <select value={group} onChange={e=>setGroup(e.target.value)} className="w-full bg-[#1a0f06] text-white p-2 rounded border border-[#3a2a14] text-xs mt-1">
              <option value="daily">Daily</option><option value="weekly">Weekly</option>
              <option value="monthly">Monthly</option><option value="yearly">Yearly</option>
            </select>
          </label>
        )}
      </div>

      <div className="flex flex-wrap gap-2 mb-4">
        <Btn onClick={load} disabled={busy} data-testid="rpt-apply">{busy?"Loading…":"Apply Filters"}</Btn>
        <Btn onClick={()=>download("csv")}  data-testid="rpt-csv"><Download size={14} className="inline mr-1"/>CSV</Btn>
        <Btn onClick={()=>download("xlsx")} data-testid="rpt-xlsx"><Download size={14} className="inline mr-1"/>Excel</Btn>
        <Btn onClick={()=>download("pdf")}  data-testid="rpt-pdf"><Download size={14} className="inline mr-1"/>PDF</Btn>
      </div>

      <div className="bg-[#0d0905] border border-[#3a2a14] rounded p-4 overflow-auto">
        {data?.error && <div className="text-red-400 text-sm">{data.error}</div>}
        {headers.length > 0 ? (
          <table className="w-full text-xs">
            <thead><tr className="text-[#f4d089]">{headers.map(h => <th key={h} className="text-left py-2 px-2 border-b border-[#3a2a14]">{h}</th>)}</tr></thead>
            <tbody>{rows.map((r,i)=>(
              <tr key={i} className="border-b border-[#1a0f06]">
                {headers.map(h => <td key={h} className="py-1.5 px-2 text-[#bfb393]">{String(r[h] ?? "")}</td>)}
              </tr>
            ))}</tbody>
          </table>
        ) : (
          <pre className="text-[10px] text-[#bfb393] whitespace-pre-wrap">{JSON.stringify(data, null, 2)}</pre>
        )}
      </div>
    </div>
  );
}

/* ---------- Settings ---------- */
function SettingsView() {
  const [cfg, setCfg] = useState(null);
  useEffect(() => { axios.get(`${API}/payments/config`).then((r) => setCfg(r.data)); }, []);
  return (
    <div data-testid="settings-view">
      <h2 className="font-display text-2xl text-white mb-1">Settings</h2>
      <p className="text-xs text-[#a89878] mb-5">System configuration & integrations.</p>
      <div className="grid md:grid-cols-2 gap-4">
        <div className="bg-[#0d0905] border border-[#3a2a14] rounded-lg p-5">
          <div className="text-[#f4d089] font-display mb-2 flex items-center gap-2"><Wallet size={16} /> Razorpay</div>
          <div className="text-sm text-[#bfb393]">Status: <span className={cfg?.enabled ? "text-[#7ad99c]" : "text-[#f4d089]"}>{cfg ? (cfg.enabled ? "LIVE" : "Placeholder / Mock") : "…"}</span></div>
          <div className="text-xs text-[#a89878] mt-1">Key ID: {cfg?.key_id || "—"}</div>
          <div className="text-xs text-[#a89878] mt-3">To enable live payments, set <code className="text-[#f4d089]">RAZORPAY_KEY_ID</code> and <code className="text-[#f4d089]">RAZORPAY_KEY_SECRET</code> in <code className="text-[#f4d089]">backend/.env</code> and restart.</div>
        </div>
        <div className="bg-[#0d0905] border border-[#3a2a14] rounded-lg p-5">
          <div className="text-[#f4d089] font-display mb-2 flex items-center gap-2"><Shield size={16} /> Tax</div>
          <div className="text-sm text-[#bfb393]">Screening GST: <span className="text-[#f4d089]">18% (CGST 9 + SGST 9)</span></div>
          <div className="text-sm text-[#bfb393]">Food GST: <span className="text-[#f4d089]">5% (billed by Billing Software)</span></div>
          <div className="text-xs text-[#a89878] mt-3">Invoices include CGST + SGST split and HSN/SAC code 9996.</div>
        </div>
        <div className="bg-[#0d0905] border border-[#3a2a14] rounded-lg p-5">
          <div className="text-[#f4d089] font-display mb-2 flex items-center gap-2"><ShieldCheck size={16} /> Slot Catalog</div>
          <div className="text-sm text-[#bfb393]">{(SLOTS || []).join(" · ")}</div>
          <div className="text-xs text-[#a89878] mt-3">Fixed Phase-1 slot grid. Per-branch custom scheduler is on the Phase-2 backlog.</div>
        </div>
        <div className="bg-[#0d0905] border border-[#3a2a14] rounded-lg p-5">
          <div className="text-[#f4d089] font-display mb-2 flex items-center gap-2"><Database size={16} /> Backups</div>
          <div className="text-sm text-[#bfb393]">Excel export available under the Reports / Export action below.</div>
          <div className="text-xs text-[#a89878] mt-3">Use the Export button in the sidebar footer to download a full snapshot.</div>
        </div>
      </div>
    </div>
  );
}

/* ---------- Activity Logs ---------- */
function ActivityLogsView({ branches }) {
  const [list, setList] = useState([]);
  const branchName = (id) => branches.find((b) => b.id === id)?.name || "—";
  useEffect(() => { api.get("/admin/bookings").then((r) => setList(r.data.slice(0, 50))); }, []);
  return (
    <div data-testid="activity-view">
      <h2 className="font-display text-2xl text-white mb-1">Activity Logs</h2>
      <p className="text-xs text-[#a89878] mb-5">Latest 50 booking events across all branches.</p>
      <div className="bg-[#0d0905] border border-[#3a2a14] rounded-lg divide-y divide-[#3a2a14]">
        {list.map((b) => (
          <div key={b.id} className="flex items-start gap-3 p-4">
            <div className="mt-0.5 text-[#f4d089]"><CalendarCheck size={16} /></div>
            <div className="flex-1">
              <div className="text-sm text-white">
                Booking <span className="text-[#f4d089]">{b.invoice_no || b.id.slice(0, 6)}</span> — {b.customer_name} ({b.guests} guests)
              </div>
              <div className="text-xs text-[#a89878] mt-0.5">
                {branchName(b.branch_id)} · {b.date} {b.slot} · ₹{b.amount} · {b.payment_status || "unpaid"}
              </div>
            </div>
            <div className="text-[10px] text-[#a89878] whitespace-nowrap">{(b.created_at || "").slice(0, 16).replace("T", " ")}</div>
          </div>
        ))}
        {list.length === 0 && <div className="p-6 text-center text-[#a89878] text-sm">No activity yet</div>}
      </div>
    </div>
  );
}

/* ---------- Contact Messages ---------- */
function ContactMessagesView() {
  const [list, setList] = useState([]);
  const [filter, setFilter] = useState("");
  const [loading, setLoading] = useState(false);
  const [editing, setEditing] = useState(null);
  const [editVal, setEditVal] = useState({ status: "new", notes: "" });

  const STATUSES = ["new", "read", "replied", "archived"];

  const load = async () => {
    setLoading(true);
    try {
      const params = filter ? { status: filter } : {};
      const r = await api.get("/admin/contact/messages", { params });
      setList(r.data || []);
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [filter]);

  const beginEdit = (m) => { setEditing(m.id); setEditVal({ status: m.status || "new", notes: m.notes || "" }); };
  const saveEdit = async (id) => {
    await api.patch(`/admin/contact/messages/${id}`, editVal);
    setEditing(null); load();
  };
  const remove = async (id) => {
    if (!window.confirm("Delete this message?")) return;
    await api.delete(`/admin/contact/messages/${id}`);
    load();
  };

  const statusColor = (s) => ({
    new: "text-[#7ad99c] border-[#7ad99c]/40",
    read: "text-[#9ec7ff] border-[#9ec7ff]/40",
    replied: "text-[#f4d089] border-[#f4d089]/40",
    archived: "text-[#a89878] border-[#3a2a14]",
  }[s] || "text-[#bfb393] border-[#3a2a14]");

  return (
    <div data-testid="contact-messages-view">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 mb-5">
        <div>
          <h2 className="text-2xl font-display text-[#f4d089]">Contact Messages</h2>
          <p className="text-sm text-[#a89878]">Submissions from the Contact Us page.</p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <Select value={filter} onChange={(e) => setFilter(e.target.value)}>
            <option value="">All statuses</option>
            {STATUSES.map((s) => <option key={s} value={s}>{s}</option>)}
          </Select>
          <Btn onClick={load} className="!from-[#3a2a14] !to-[#2a1d0d] !text-[#f4d089] border border-[#d4a574]/30">Refresh</Btn>
        </div>
      </div>

      <div className="rounded-xl border border-[#3a2a14] overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-[#1a0f06] text-[#a89878]">
            <tr>
              {["Created", "Name", "Phone", "Email", "Subject", "Message", "Status", "Notes", ""].map((h) => (
                <th key={h} className="text-left px-3 py-2 text-[11px] tracking-widest uppercase whitespace-nowrap">{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {list.map((m) => (
              <tr key={m.id} className="border-t border-[#3a2a14]/60 align-top">
                <td className="px-3 py-2 text-xs text-[#bfb393] whitespace-nowrap">{(m.created_at || "").slice(0, 16).replace("T", " ")}</td>
                <td className="px-3 py-2 text-white font-medium whitespace-nowrap">{m.name}</td>
                <td className="px-3 py-2 text-[#d8cdb3] whitespace-nowrap">{m.phone}</td>
                <td className="px-3 py-2 text-[#bfb393]">{m.email || "—"}</td>
                <td className="px-3 py-2 text-[#bfb393]">{m.subject || "—"}</td>
                <td className="px-3 py-2 max-w-[320px] text-[#d8cdb3] whitespace-normal">{m.message}</td>
                <td className="px-3 py-2">
                  {editing === m.id ? (
                    <Select value={editVal.status} onChange={(e) => setEditVal({ ...editVal, status: e.target.value })}>
                      {STATUSES.map((s) => <option key={s} value={s}>{s}</option>)}
                    </Select>
                  ) : (
                    <span className={`text-[11px] tracking-widest uppercase border px-2 py-0.5 rounded ${statusColor(m.status)}`}>{m.status}</span>
                  )}
                </td>
                <td className="px-3 py-2 max-w-[220px]">
                  {editing === m.id ? (
                    <Input value={editVal.notes} onChange={(e) => setEditVal({ ...editVal, notes: e.target.value })} placeholder="Internal notes" />
                  ) : (
                    <span className="text-[12px] text-[#bfb393] whitespace-normal">{m.notes || "—"}</span>
                  )}
                </td>
                <td className="px-3 py-2 whitespace-nowrap">
                  {editing === m.id ? (
                    <div className="flex gap-2">
                      <Btn className="!px-3 !py-1.5 text-xs" onClick={() => saveEdit(m.id)}>Save</Btn>
                      <button className="text-xs text-[#a89878] hover:text-white" onClick={() => setEditing(null)}>Cancel</button>
                    </div>
                  ) : (
                    <div className="flex gap-3">
                      <button className="text-xs text-[#f4d089] hover:underline" onClick={() => beginEdit(m)}>Edit</button>
                      <button className="text-xs text-red-400 hover:underline" onClick={() => remove(m.id)}>
                        <Trash2 size={14} className="inline" />
                      </button>
                    </div>
                  )}
                </td>
              </tr>
            ))}
            {list.length === 0 && (
              <tr><td colSpan={9} className="text-center text-[#a89878] text-sm py-8">{loading ? "Loading…" : "No messages yet"}</td></tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}                    
/* ---------- Franchise Leads ---------- */
function FranchiseLeadsView() {
  const [list, setList] = useState([]);
  const [filter, setFilter] = useState("");
  const [loading, setLoading] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [editing, setEditing] = useState(null); // lead.id
  const [editVal, setEditVal] = useState({ status: "new", notes: "" });

  const STATUSES = ["new", "contacted", "qualified", "converted", "rejected"];

  const load = async () => {
    setLoading(true);
    try {
      const params = filter ? { status: filter } : {};
      const r = await api.get("/admin/franchise/leads", { params });
      setList(r.data || []);
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [filter]);

  const beginEdit = (l) => { setEditing(l.id); setEditVal({ status: l.status || "new", notes: l.notes || "" }); };
  const saveEdit = async (id) => {
    await api.patch(`/admin/franchise/leads/${id}`, editVal);
    setEditing(null);
    load();
  };
  const remove = async (id) => {
    if (!window.confirm("Delete this lead?")) return;
    await api.delete(`/admin/franchise/leads/${id}`);
    load();
  };

  const exportExcel = async () => {
    setExporting(true);
    try {
      const t = localStorage.getItem(TOKEN_KEY);
      const res = await fetch(`${API}/admin/franchise/leads/export`, {
        headers: { "X-Admin-Token": t },
      });
      if (!res.ok) throw new Error("Export failed");
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `franchise_leads_${new Date().toISOString().slice(0, 10)}.xlsx`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch (e) {
      alert("Export failed");
    } finally { setExporting(false); }
  };

  const statusColor = (s) => ({
    new: "text-[#7ad99c] border-[#7ad99c]/40",
    contacted: "text-[#f4d089] border-[#f4d089]/40",
    qualified: "text-[#9ec7ff] border-[#9ec7ff]/40",
    converted: "text-[#7ad99c] border-[#7ad99c]/60 bg-[#0f1f12]",
    rejected: "text-[#ff8a8a] border-[#ff8a8a]/40",
  }[s] || "text-[#bfb393] border-[#3a2a14]");

  return (
    <div data-testid="franchise-leads-view">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 mb-5">
        <div>
          <h2 className="text-2xl font-display text-[#f4d089]">Franchise Leads</h2>
          <p className="text-sm text-[#a89878]">Enquiries submitted via the Franchise page.</p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <Select data-testid="franchise-filter-status" value={filter} onChange={(e) => setFilter(e.target.value)}>
            <option value="">All statuses</option>
            {STATUSES.map((s) => <option key={s} value={s}>{s}</option>)}
          </Select>
          <Btn data-testid="franchise-refresh" onClick={load} className="!from-[#3a2a14] !to-[#2a1d0d] !text-[#f4d089] border border-[#d4a574]/30">Refresh</Btn>
          <Btn data-testid="franchise-export-excel" onClick={exportExcel} disabled={exporting}>
            <span className="inline-flex items-center gap-2"><Download size={14} /> {exporting ? "Exporting…" : "Export Excel"}</span>
          </Btn>
        </div>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-5 gap-3 mb-5">
        {STATUSES.map((s) => {
          const count = list.filter((l) => l.status === s).length;
          return (
            <div key={s} className={`rounded-md border px-3 py-2 ${statusColor(s)}`}>
              <div className="text-[10px] tracking-widest uppercase">{s}</div>
              <div className="text-xl font-display">{count}</div>
            </div>
          );
        })}
      </div>

      <div className="rounded-xl border border-[#3a2a14] overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-[#1a0f06] text-[#a89878]">
            <tr>
              {["Created", "Name", "Phone", "Email", "City", "Investment", "Space", "Timeline", "Status", "Notes", ""].map((h) => (
                <th key={h} className="text-left px-3 py-2 text-[11px] tracking-widest uppercase whitespace-nowrap">{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {list.map((l) => (
              <tr key={l.id} className="border-t border-[#3a2a14]/60 align-top" data-testid={`franchise-lead-${l.id}`}>
                <td className="px-3 py-2 text-xs text-[#bfb393] whitespace-nowrap">{(l.created_at || "").slice(0, 16).replace("T", " ")}</td>
                <td className="px-3 py-2 text-white font-medium whitespace-nowrap">
                  {l.name}
                  {l.message && <div className="text-[11px] text-[#a89878] mt-0.5 max-w-[260px] whitespace-normal">{l.message}</div>}
                </td>
                <td className="px-3 py-2 text-[#d8cdb3] whitespace-nowrap">{l.phone}</td>
                <td className="px-3 py-2 text-[#bfb393]">{l.email || "—"}</td>
                <td className="px-3 py-2 text-[#bfb393]">{l.city || "—"}</td>
                <td className="px-3 py-2 text-[#bfb393]">{l.investment || "—"}</td>
                <td className="px-3 py-2 text-[#bfb393]">{l.space_available || "—"}</td>
                <td className="px-3 py-2 text-[#bfb393]">{l.timeline || "—"}</td>
                <td className="px-3 py-2">
                  {editing === l.id ? (
                    <Select value={editVal.status} onChange={(e) => setEditVal({ ...editVal, status: e.target.value })} data-testid={`edit-status-${l.id}`}>
                      {STATUSES.map((s) => <option key={s} value={s}>{s}</option>)}
                    </Select>
                  ) : (
                    <span className={`text-[11px] tracking-widest uppercase border px-2 py-0.5 rounded ${statusColor(l.status)}`}>{l.status}</span>
                  )}
                </td>
                <td className="px-3 py-2 max-w-[220px]">
                  {editing === l.id ? (
                    <Input value={editVal.notes} onChange={(e) => setEditVal({ ...editVal, notes: e.target.value })} placeholder="Internal notes" data-testid={`edit-notes-${l.id}`} />
                  ) : (
                    <span className="text-[12px] text-[#bfb393] whitespace-normal">{l.notes || "—"}</span>
                  )}
                </td>
                <td className="px-3 py-2 whitespace-nowrap">
                  {editing === l.id ? (
                    <div className="flex gap-2">
                      <Btn className="!px-3 !py-1.5 text-xs" onClick={() => saveEdit(l.id)} data-testid={`save-${l.id}`}>Save</Btn>
                      <button className="text-xs text-[#a89878] hover:text-white" onClick={() => setEditing(null)}>Cancel</button>
                    </div>
                  ) : (
                    <div className="flex gap-3">
                      <button className="text-xs text-[#f4d089] hover:underline" onClick={() => beginEdit(l)} data-testid={`edit-${l.id}`}>Edit</button>
                      <button className="text-xs text-red-400 hover:underline" onClick={() => remove(l.id)} data-testid={`del-${l.id}`}>
                        <Trash2 size={14} className="inline" />
                      </button>
                    </div>
                  )}
                </td>
              </tr>
            ))}
            {list.length === 0 && (
              <tr><td colSpan={11} className="text-center text-[#a89878] text-sm py-8">{loading ? "Loading…" : "No franchise leads yet"}</td></tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}

/* ---------- Property Leads (List Your Property) ---------- */
function PropertyLeadsView() {
  const [list, setList] = useState([]);
  const [filter, setFilter] = useState("");
  const [loading, setLoading] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [editing, setEditing] = useState(null);
  const [editVal, setEditVal] = useState({ status: "new", notes: "" });

  const STATUSES = ["new", "contacted", "qualified", "onboarded", "rejected"];

  const load = async () => {
    setLoading(true);
    try {
      const params = filter ? { status: filter } : {};
      const r = await api.get("/admin/property/leads", { params });
      setList(r.data || []);
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [filter]);

  const beginEdit = (l) => { setEditing(l.id); setEditVal({ status: l.status || "new", notes: l.notes || "" }); };
  const saveEdit = async (id) => {
    await api.patch(`/admin/property/leads/${id}`, editVal);
    setEditing(null);
    load();
  };
  const remove = async (id) => {
    if (!window.confirm("Delete this lead?")) return;
    await api.delete(`/admin/property/leads/${id}`);
    load();
  };

  const exportFile = async (fmt) => {
    setExporting(true);
    try {
      const t = localStorage.getItem(TOKEN_KEY);
      const res = await fetch(`${API}/admin/property/leads/export?format=${fmt}`, {
        headers: { "X-Admin-Token": t },
      });
      if (!res.ok) throw new Error("Export failed");
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `property_leads_${new Date().toISOString().slice(0, 10)}.${fmt}`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch (e) {
      alert("Export failed");
    } finally { setExporting(false); }
  };

  const statusColor = (s) => ({
    new: "text-[#7ad99c] border-[#7ad99c]/40",
    contacted: "text-[#f4d089] border-[#f4d089]/40",
    qualified: "text-[#9ec7ff] border-[#9ec7ff]/40",
    onboarded: "text-[#7ad99c] border-[#7ad99c]/60 bg-[#0f1f12]",
    rejected: "text-[#ff8a8a] border-[#ff8a8a]/40",
  }[s] || "text-[#bfb393] border-[#3a2a14]");

  return (
    <div data-testid="property-leads-view">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 mb-5">
        <div>
          <h2 className="text-2xl font-display text-[#f4d089]">Property Leads</h2>
          <p className="text-sm text-[#a89878]">Enquiries submitted via the List Your Property page.</p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <Select data-testid="property-filter-status" value={filter} onChange={(e) => setFilter(e.target.value)}>
            <option value="">All statuses</option>
            {STATUSES.map((s) => <option key={s} value={s}>{s}</option>)}
          </Select>
          <Btn data-testid="property-refresh" onClick={load} className="!from-[#3a2a14] !to-[#2a1d0d] !text-[#f4d089] border border-[#d4a574]/30">Refresh</Btn>
          <Btn data-testid="property-export-excel" onClick={() => exportFile("xlsx")} disabled={exporting}>
            <span className="inline-flex items-center gap-2"><Download size={14} /> {exporting ? "…" : "Export Excel"}</span>
          </Btn>
          <Btn data-testid="property-export-csv" onClick={() => exportFile("csv")} disabled={exporting} className="!from-[#3a2a14] !to-[#2a1d0d] !text-[#f4d089] border border-[#d4a574]/30">
            <span className="inline-flex items-center gap-2"><Download size={14} /> CSV</span>
          </Btn>
        </div>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-5 gap-3 mb-5">
        {STATUSES.map((s) => {
          const count = list.filter((l) => l.status === s).length;
          return (
            <div key={s} className={`rounded-md border px-3 py-2 ${statusColor(s)}`}>
              <div className="text-[10px] tracking-widest uppercase">{s}</div>
              <div className="text-xl font-display">{count}</div>
            </div>
          );
        })}
      </div>

      <div className="rounded-xl border border-[#3a2a14] overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-[#1a0f06] text-[#a89878]">
            <tr>
              {["Created", "Name", "Phone", "Email", "City", "Type", "Location", "Capacity", "Area", "Avail.", "Status", "Notes", ""].map((h) => (
                <th key={h} className="text-left px-3 py-2 text-[11px] tracking-widest uppercase whitespace-nowrap">{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {list.map((l) => (
              <tr key={l.id} className="border-t border-[#3a2a14]/60 align-top" data-testid={`property-lead-${l.id}`}>
                <td className="px-3 py-2 text-xs text-[#bfb393] whitespace-nowrap">{(l.created_at || "").slice(0, 16).replace("T", " ")}</td>
                <td className="px-3 py-2 text-white font-medium whitespace-nowrap">
                  {l.name}
                  {l.message && <div className="text-[11px] text-[#a89878] mt-0.5 max-w-[260px] whitespace-normal">{l.message}</div>}
                </td>
                <td className="px-3 py-2 text-[#d8cdb3] whitespace-nowrap">{l.phone}</td>
                <td className="px-3 py-2 text-[#bfb393]">{l.email || "—"}</td>
                <td className="px-3 py-2 text-[#bfb393]">{l.city || "—"}</td>
                <td className="px-3 py-2 text-[#bfb393]">{l.property_type || "—"}</td>
                <td className="px-3 py-2 text-[#bfb393]">{l.location || "—"}</td>
                <td className="px-3 py-2 text-[#bfb393]">{l.capacity || "—"}</td>
                <td className="px-3 py-2 text-[#bfb393]">{l.area_sqft || "—"}</td>
                <td className="px-3 py-2 text-[#bfb393]">{l.availability || "—"}</td>
                <td className="px-3 py-2">
                  {editing === l.id ? (
                    <Select value={editVal.status} onChange={(e) => setEditVal({ ...editVal, status: e.target.value })} data-testid={`p-edit-status-${l.id}`}>
                      {STATUSES.map((s) => <option key={s} value={s}>{s}</option>)}
                    </Select>
                  ) : (
                    <span className={`text-[11px] tracking-widest uppercase border px-2 py-0.5 rounded ${statusColor(l.status)}`}>{l.status}</span>
                  )}
                </td>
                <td className="px-3 py-2 max-w-[220px]">
                  {editing === l.id ? (
                    <Input value={editVal.notes} onChange={(e) => setEditVal({ ...editVal, notes: e.target.value })} placeholder="Internal notes" data-testid={`p-edit-notes-${l.id}`} />
                  ) : (
                    <span className="text-[12px] text-[#bfb393] whitespace-normal">{l.notes || "—"}</span>
                  )}
                </td>
                <td className="px-3 py-2 whitespace-nowrap">
                  {editing === l.id ? (
                    <div className="flex gap-2">
                      <Btn className="!px-3 !py-1.5 text-xs" onClick={() => saveEdit(l.id)} data-testid={`p-save-${l.id}`}>Save</Btn>
                      <button className="text-xs text-[#a89878] hover:text-white" onClick={() => setEditing(null)}>Cancel</button>
                    </div>
                  ) : (
                    <div className="flex gap-3">
                      <button className="text-xs text-[#f4d089] hover:underline" onClick={() => beginEdit(l)} data-testid={`p-edit-${l.id}`}>Edit</button>
                      <button className="text-xs text-red-400 hover:underline" onClick={() => remove(l.id)} data-testid={`p-del-${l.id}`}>
                        <Trash2 size={14} className="inline" />
                      </button>
                    </div>
                  )}
                </td>
              </tr>
            ))}
            {list.length === 0 && (
              <tr><td colSpan={13} className="text-center text-[#a89878] text-sm py-8">{loading ? "Loading…" : "No property leads yet"}</td></tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}

/* ---------- About Page Content (super-admin editable) ---------- */
const AboutField = ({ label, value, onChange, multiline = false, testid }) => (
  <div>
    <label className="text-[11px] tracking-widest uppercase text-[#a89878] block mb-1">{label}</label>
    {multiline ? (
      <textarea
        value={value || ""}
        onChange={(e) => onChange(e.target.value)}
        rows={3}
        data-testid={testid}
        className="w-full bg-[#0a0604] border border-[#3a2a14] rounded-md px-3 py-2 text-sm text-white focus:outline-none focus:border-[#d4a574]"
      />
    ) : (
      <Input value={value || ""} onChange={(e) => onChange(e.target.value)} data-testid={testid} className="w-full" />
    )}
  </div>
);

const AboutGroup = ({ title, children }) => (
  <div className="border border-[#3a2a14] rounded-xl p-5 bg-[#0d0905]">
    <h3 className="text-[#f4d089] font-display text-lg mb-4">{title}</h3>
    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">{children}</div>
  </div>
);

function AboutContentView() {
  const [c, setC] = useState(null);
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState("");

  const load = async () => {
    try {
      const r = await api.get("/admin/content/about");
      setC(r.data);
    } catch {
      setMsg("Failed to load content");
    }
  };
  useEffect(() => { load(); }, []);

  const set = (k, v) => setC((p) => ({ ...p, [k]: v }));

  const setItem = (k, idx, field, val) => {
    setC((p) => {
      const arr = [...(p[k] || [])];
      arr[idx] = { ...arr[idx], [field]: val };
      return { ...p, [k]: arr };
    });
  };

  const save = async () => {
    setBusy(true); setMsg("");
    try {
      await api.put("/admin/content/about", c);
      setMsg("Saved successfully");
      setTimeout(() => setMsg(""), 2500);
    } catch {
      setMsg("Save failed");
    }
    setBusy(false);
  };

  const reload = async () => {
    setBusy(true); setMsg("");
    try {
      const r = await api.get("/admin/content/about");
      setC(r.data);
      setMsg("Reloaded latest saved content");
      setTimeout(() => setMsg(""), 2000);
    } catch {
      setMsg("Reload failed");
    }
    setBusy(false);
  };

  if (!c) return <div className="text-[#bfb393] text-sm">Loading…</div>;

  const Field = AboutField;
  const Group = AboutGroup;

  return (
    <div data-testid="about-content-view">
      <div className="flex items-center justify-between mb-5 flex-wrap gap-3">
        <div>
          <h2 className="font-display text-2xl text-white">About Page Content</h2>
          <p className="text-xs text-[#a89878]">Edit any text shown on the public /about page. Changes go live immediately after Save.</p>
        </div>
        <div className="flex gap-2 items-center">
          {msg && <span className="text-xs text-[#7ad99c]" data-testid="about-save-msg">{msg}</span>}
          <Btn onClick={reload} className="!bg-transparent !text-[#bfb393] border border-[#3a2a14]" data-testid="about-reload-btn">Reload</Btn>
          <Btn onClick={save} disabled={busy} data-testid="about-save-btn">{busy ? "Saving…" : "Save changes"}</Btn>
        </div>
      </div>

      <div className="space-y-5">
        <Group title="Hero">
          <Field label="Eyebrow tag" value={c.eyebrow} onChange={(v) => set("eyebrow", v)} testid="f-eyebrow" />
          <div />
          <Field label="Title — text before highlight" value={c.title_pre} onChange={(v) => set("title_pre", v)} testid="f-title-pre" />
          <Field label="Title — highlighted word" value={c.title_highlight} onChange={(v) => set("title_highlight", v)} testid="f-title-highlight" />
          <Field label="Title — text after highlight" value={c.title_post} onChange={(v) => set("title_post", v)} testid="f-title-post" />
          <div />
          <div className="md:col-span-2">
            <Field label="Subtitle paragraph" value={c.subtitle} onChange={(v) => set("subtitle", v)} multiline testid="f-subtitle" />
          </div>
        </Group>

        <Group title="Hero image strip">
          <Field label="Hero eyebrow" value={c.hero_eyebrow} onChange={(v) => set("hero_eyebrow", v)} testid="f-hero-eyebrow" />
          <Field label="Hero title" value={c.hero_title} onChange={(v) => set("hero_title", v)} testid="f-hero-title" />
          <Field label="Hero rating line" value={c.hero_rating} onChange={(v) => set("hero_rating", v)} testid="f-hero-rating" />
        </Group>

        <Group title="Our Story">
          <Field label="Story eyebrow" value={c.story_eyebrow} onChange={(v) => set("story_eyebrow", v)} testid="f-story-eyebrow" />
          <Field label="Story heading" value={c.story_heading} onChange={(v) => set("story_heading", v)} testid="f-story-heading" />
          <div className="md:col-span-2">
            <Field label="Paragraph 1" value={c.story_p1} onChange={(v) => set("story_p1", v)} multiline testid="f-story-p1" />
          </div>
          <div className="md:col-span-2">
            <Field label="Paragraph 2" value={c.story_p2} onChange={(v) => set("story_p2", v)} multiline testid="f-story-p2" />
          </div>
          <div className="md:col-span-2">
            <Field label="Paragraph 3 — text before highlight" value={c.story_p3_pre} onChange={(v) => set("story_p3_pre", v)} multiline testid="f-story-p3-pre" />
          </div>
          <Field label="Paragraph 3 — highlighted phrase" value={c.story_p3_highlight} onChange={(v) => set("story_p3_highlight", v)} testid="f-story-p3-highlight" />
          <Field label="Paragraph 3 — text after highlight" value={c.story_p3_post} onChange={(v) => set("story_p3_post", v)} testid="f-story-p3-post" />
        </Group>

        <Group title="Stats (4 items)">
          {(c.stats || []).map((s, i) => (
            <React.Fragment key={i}>
              <Field label={`Stat ${i + 1} — value`} value={s.v} onChange={(v) => setItem("stats", i, "v", v)} testid={`f-stat-v-${i}`} />
              <Field label={`Stat ${i + 1} — label`} value={s.l} onChange={(v) => setItem("stats", i, "l", v)} testid={`f-stat-l-${i}`} />
            </React.Fragment>
          ))}
        </Group>

        <Group title="What we stand for">
          <Field label="Eyebrow" value={c.values_eyebrow} onChange={(v) => set("values_eyebrow", v)} testid="f-values-eyebrow" />
          <Field label="Heading" value={c.values_heading} onChange={(v) => set("values_heading", v)} testid="f-values-heading" />
          {(c.values || []).map((v, i) => (
            <React.Fragment key={i}>
              <Field label={`Value ${i + 1} — title`} value={v.title} onChange={(val) => setItem("values", i, "title", val)} testid={`f-value-title-${i}`} />
              <Field label={`Value ${i + 1} — description`} value={v.desc} onChange={(val) => setItem("values", i, "desc", val)} multiline testid={`f-value-desc-${i}`} />
            </React.Fragment>
          ))}
        </Group>

        <Group title="The Journey (timeline)">
          <Field label="Eyebrow" value={c.timeline_eyebrow} onChange={(v) => set("timeline_eyebrow", v)} testid="f-timeline-eyebrow" />
          <Field label="Heading" value={c.timeline_heading} onChange={(v) => set("timeline_heading", v)} testid="f-timeline-heading" />
          {(c.milestones || []).map((m, i) => (
            <React.Fragment key={i}>
              <Field label={`Milestone ${i + 1} — year`} value={m.year} onChange={(v) => setItem("milestones", i, "year", v)} testid={`f-ms-year-${i}`} />
              <Field label={`Milestone ${i + 1} — title`} value={m.title} onChange={(v) => setItem("milestones", i, "title", v)} testid={`f-ms-title-${i}`} />
              <div className="md:col-span-2">
                <Field label={`Milestone ${i + 1} — description`} value={m.desc} onChange={(v) => setItem("milestones", i, "desc", v)} multiline testid={`f-ms-desc-${i}`} />
              </div>
            </React.Fragment>
          ))}
        </Group>

        <Group title="Quick info row (3 cards)">
          {(c.quick_info || []).map((q, i) => (
            <React.Fragment key={i}>
              <Field label={`Card ${i + 1} — title`} value={q.title} onChange={(v) => setItem("quick_info", i, "title", v)} testid={`f-quick-title-${i}`} />
              <Field label={`Card ${i + 1} — description`} value={q.desc} onChange={(v) => setItem("quick_info", i, "desc", v)} testid={`f-quick-desc-${i}`} />
            </React.Fragment>
          ))}
        </Group>

        <Group title="Call to action & footer">
          <Field label="CTA heading" value={c.cta_heading} onChange={(v) => set("cta_heading", v)} testid="f-cta-heading" />
          <Field label="CTA subtitle" value={c.cta_subtitle} onChange={(v) => set("cta_subtitle", v)} testid="f-cta-subtitle" />
          <Field label="Primary button label" value={c.cta_book_btn} onChange={(v) => set("cta_book_btn", v)} testid="f-cta-book" />
          <Field label="Secondary button label" value={c.cta_franchise_btn} onChange={(v) => set("cta_franchise_btn", v)} testid="f-cta-franchise" />
          <Field label="Footer text (after © year)" value={c.footer_text} onChange={(v) => set("footer_text", v)} testid="f-footer" />
        </Group>

        <div className="flex justify-end pt-2">
          <Btn onClick={save} disabled={busy} data-testid="about-save-btn-bottom">{busy ? "Saving…" : "Save changes"}</Btn>
        </div>
      </div>
    </div>
  );
}


/* ---------- Contact Info (Super-admin editable) ---------- */
function ContactInfoView() {
  const [c, setC] = useState(null);
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState("");

  const load = async () => {
    try {
      const r = await api.get("/admin/content/contact");
      setC(r.data || {});
    } catch (e) {
      setMsg("Failed to load");
    }
  };
  useEffect(() => { load(); }, []);

  const set = (k) => (e) => setC({ ...c, [k]: e.target.value });

  const save = async () => {
    if (!c) return;
    setBusy(true); setMsg("");
    try {
      await api.put("/admin/content/contact", c);
      setMsg("Saved ✓");
      setTimeout(() => setMsg(""), 2500);
    } catch (e) {
      setMsg("Save failed");
    } finally {
      setBusy(false);
    }
  };

  if (!c) return <div className="text-[#bfb393] text-sm">Loading…</div>;

  const Row = ({ label, k, placeholder }) => (
    <div>
      <label className="text-xs tracking-wider text-[#cbbf9f]">{label}</label>
      <input
        value={c[k] || ""}
        onChange={set(k)}
        placeholder={placeholder || ""}
        data-testid={`contact-${k}`}
        className="mt-2 w-full bg-[#0d0905] border border-[#3a2a14] rounded-md px-4 py-2 text-[#e8dcc0] focus:outline-none focus:border-[#d4a574]"
      />
    </div>
  );

  return (
    <div data-testid="contact-info-view">
      <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
        <div>
          <h2 className="font-display text-2xl text-white">Contact Info</h2>
          <p className="text-xs text-[#a89878]">Edit WhatsApp / Call / Email / Instagram shown on the Contact page. The WhatsApp number is also used by every WhatsApp button across the site.</p>
        </div>
        <div className="flex items-center gap-2">
          {msg && <span className="text-xs text-[#7ad99c]" data-testid="contact-save-msg">{msg}</span>}
          <Btn onClick={load} className="!bg-transparent !text-[#bfb393] border border-[#3a2a14]" data-testid="contact-reload-btn">Reload</Btn>
          <Btn onClick={save} disabled={busy} data-testid="contact-save-btn">{busy ? "Saving…" : "Save changes"}</Btn>
        </div>
      </div>

      <div className="grid sm:grid-cols-2 gap-4 rounded-xl border border-[#3a2a14]/70 bg-gradient-to-b from-[#14100a] to-[#0d0905] p-5">
        <div className="sm:col-span-2 text-[#f4d089] text-sm font-semibold">WhatsApp</div>
        <Row label="Sub-text"        k="whatsapp_label"          placeholder="Chat with us instantly" />
        <Row label="Display number"  k="whatsapp_number_display" placeholder="+91 98765 43210" />
        <Row label="Dial number (digits only, with country code – used by wa.me)" k="whatsapp_number_dial" placeholder="919876543210" />

        <div className="sm:col-span-2 text-[#f4d089] text-sm font-semibold mt-4">Call Us</div>
        <Row label="Sub-text"        k="call_label"          placeholder="Mon – Sun: 10:00 AM – 11:00 PM" />
        <Row label="Display number"  k="call_number_display" placeholder="+91 98765 43210" />
        <Row label="Dial number (used by tel:)" k="call_number_dial" placeholder="+919876543210" />

        <div className="sm:col-span-2 text-[#f4d089] text-sm font-semibold mt-4">Email Us</div>
        <Row label="Sub-text" k="email_label" placeholder="We reply within 30 minutes" />
        <Row label="Email"    k="email"       placeholder="hello@cinevibe.in" />

        <div className="sm:col-span-2 text-[#f4d089] text-sm font-semibold mt-4">Instagram</div>
        <Row label="Sub-text"      k="instagram_label"  placeholder="DM us on Instagram" />
        <Row label="Handle"        k="instagram_handle" placeholder="@cinevibe.india" />
        <Row label="Profile URL"   k="instagram_url"    placeholder="https://instagram.com/cinevibe.india" />
      </div>
    </div>
  );
}

/* ---------- List-Your-Space Page Content (super-admin editable) ---------- */
function ListPropertyContentView() {
  const [c, setC] = useState(null);
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState("");

  const load = async () => {
    try {
      const r = await api.get("/admin/content/list_property");
      setC(r.data);
    } catch {
      setMsg("Failed to load content");
    }
  };
  useEffect(() => { load(); }, []);

  const set = (k, v) => setC((p) => ({ ...p, [k]: v }));

  const setItem = (k, idx, field, val) => {
    setC((p) => {
      const arr = [...(p[k] || [])];
      arr[idx] = { ...arr[idx], [field]: val };
      return { ...p, [k]: arr };
    });
  };

  const save = async () => {
    setBusy(true); setMsg("");
    try {
      await api.put("/admin/content/list_property", c);
      setMsg("Saved successfully");
      setTimeout(() => setMsg(""), 2500);
    } catch {
      setMsg("Save failed");
    }
    setBusy(false);
  };

  if (!c) return <div className="text-[#bfb393] text-sm">Loading…</div>;

  const Field = AboutField;
  const Group = AboutGroup;

  return (
    <div data-testid="list-property-content-view">
      <div className="flex items-center justify-between mb-5 flex-wrap gap-3">
        <div>
          <h2 className="font-display text-2xl text-white">List-Your-Space Page Content</h2>
          <p className="text-xs text-[#a89878]">Edit every text shown on the public /list-property page. Changes go live after Save.</p>
        </div>
        <div className="flex gap-2 items-center">
          {msg && <span className="text-xs text-[#7ad99c]" data-testid="lp-save-msg">{msg}</span>}
          <Btn onClick={load} className="!bg-transparent !text-[#bfb393] border border-[#3a2a14]" data-testid="lp-reload-btn">Reload</Btn>
          <Btn onClick={save} disabled={busy} data-testid="lp-save-btn">{busy ? "Saving…" : "Save changes"}</Btn>
        </div>
      </div>

      <div className="space-y-5">
        <Group title="Header">
          <Field label="Back link label"  value={c.header_back_label} onChange={(v) => set("header_back_label", v)} testid="f-lp-header-back" />
          <Field label="CTA button label" value={c.header_cta_label}  onChange={(v) => set("header_cta_label", v)}  testid="f-lp-header-cta" />
        </Group>

        <Group title="Hero">
          <Field label="Eyebrow tag"                  value={c.hero_eyebrow}         onChange={(v) => set("hero_eyebrow", v)}         testid="f-lp-hero-eyebrow" />
          <div />
          <Field label="Title — text before highlight" value={c.hero_title_pre}       onChange={(v) => set("hero_title_pre", v)}       testid="f-lp-title-pre" />
          <Field label="Title — highlighted word"      value={c.hero_title_highlight} onChange={(v) => set("hero_title_highlight", v)} testid="f-lp-title-hi" />
          <Field label="Title — text after highlight"  value={c.hero_title_post}      onChange={(v) => set("hero_title_post", v)}      testid="f-lp-title-post" />
          <Field label="Hero CTA label"                value={c.hero_cta_label}       onChange={(v) => set("hero_cta_label", v)}       testid="f-lp-hero-cta" />
          <div className="md:col-span-2">
            <Field label="Hero subtitle" value={c.hero_subtitle} onChange={(v) => set("hero_subtitle", v)} multiline testid="f-lp-hero-sub" />
          </div>
        </Group>

        <Group title="Hero Stats (4 cards)">
          {[1, 2, 3, 4].map((i) => (
            <React.Fragment key={i}>
              <Field label={`Stat ${i} — value`} value={c[`stat${i}_value`]} onChange={(v) => set(`stat${i}_value`, v)} testid={`f-lp-stat${i}-v`} />
              <Field label={`Stat ${i} — label`} value={c[`stat${i}_label`]} onChange={(v) => set(`stat${i}_label`, v)} testid={`f-lp-stat${i}-l`} />
            </React.Fragment>
          ))}
        </Group>

        <Group title="Why list with us">
          <Field label="Eyebrow"  value={c.why_eyebrow}  onChange={(v) => set("why_eyebrow", v)}  testid="f-lp-why-eyebrow" />
          <Field label="Heading"  value={c.why_heading}  onChange={(v) => set("why_heading", v)}  testid="f-lp-why-heading" />
          <div className="md:col-span-2">
            <Field label="Subtitle" value={c.why_subtitle} onChange={(v) => set("why_subtitle", v)} multiline testid="f-lp-why-sub" />
          </div>
          {(c.features || []).map((it, idx) => (
            <React.Fragment key={idx}>
              <Field label={`Feature ${idx + 1} — title`} value={it.title} onChange={(v) => setItem("features", idx, "title", v)} testid={`f-lp-feat-t-${idx}`} />
              <Field label={`Feature ${idx + 1} — desc`}  value={it.desc}  onChange={(v) => setItem("features", idx, "desc", v)}  multiline testid={`f-lp-feat-d-${idx}`} />
            </React.Fragment>
          ))}
        </Group>

        <Group title="How it works (4 steps)">
          <Field label="Eyebrow" value={c.how_eyebrow} onChange={(v) => set("how_eyebrow", v)} testid="f-lp-how-eyebrow" />
          <Field label="Heading" value={c.how_heading} onChange={(v) => set("how_heading", v)} testid="f-lp-how-heading" />
          {(c.steps || []).map((it, idx) => (
            <React.Fragment key={idx}>
              <Field label={`Step ${idx + 1} — title`} value={it.title} onChange={(v) => setItem("steps", idx, "title", v)} testid={`f-lp-step-t-${idx}`} />
              <Field label={`Step ${idx + 1} — desc`}  value={it.desc}  onChange={(v) => setItem("steps", idx, "desc", v)}  multiline testid={`f-lp-step-d-${idx}`} />
            </React.Fragment>
          ))}
        </Group>

        <Group title="Contact / form intro">
          <Field label="Eyebrow"  value={c.form_eyebrow}  onChange={(v) => set("form_eyebrow", v)}  testid="f-lp-form-eyebrow" />
          <Field label="Heading"  value={c.form_heading}  onChange={(v) => set("form_heading", v)}  testid="f-lp-form-heading" />
          <div className="md:col-span-2">
            <Field label="Subtitle" value={c.form_subtitle} onChange={(v) => set("form_subtitle", v)} multiline testid="f-lp-form-sub" />
          </div>
          <Field label="Phone"    value={c.form_phone}    onChange={(v) => set("form_phone", v)}    testid="f-lp-form-phone" />
          <Field label="Email"    value={c.form_email}    onChange={(v) => set("form_email", v)}    testid="f-lp-form-email" />
          <div className="md:col-span-2">
            <Field label="Location line" value={c.form_location} onChange={(v) => set("form_location", v)} testid="f-lp-form-location" />
          </div>
        </Group>

        <Group title="Enquiry form card">
          <Field label="Eyebrow"        value={c.form_card_eyebrow}  onChange={(v) => set("form_card_eyebrow", v)}  testid="f-lp-card-eyebrow" />
          <Field label="Heading"        value={c.form_card_heading}  onChange={(v) => set("form_card_heading", v)}  testid="f-lp-card-heading" />
          <div className="md:col-span-2">
            <Field label="Subtitle"     value={c.form_card_subtitle} onChange={(v) => set("form_card_subtitle", v)} multiline testid="f-lp-card-sub" />
          </div>
          <Field label="Submit button label" value={c.form_submit_label} onChange={(v) => set("form_submit_label", v)} testid="f-lp-submit" />
          <div className="md:col-span-2">
            <Field label="Disclaimer text"   value={c.form_disclaimer}   onChange={(v) => set("form_disclaimer", v)}   multiline testid="f-lp-disclaimer" />
          </div>
          <Field label="Success — title prefix (e.g. Thank you,)" value={c.form_success_title} onChange={(v) => set("form_success_title", v)} testid="f-lp-success-t" />
          <div className="md:col-span-2">
            <Field label="Success — message" value={c.form_success_msg} onChange={(v) => set("form_success_msg", v)} multiline testid="f-lp-success-m" />
          </div>
        </Group>

        <Group title="Footer">
          <div className="md:col-span-2">
            <Field label="Footer text (after © year)" value={c.footer_text} onChange={(v) => set("footer_text", v)} testid="f-lp-footer" />
          </div>
        </Group>
      </div>
    </div>
  );
}


/* ---------- Franchise Page Content (Super-admin only) ---------- */
function FranchiseContentView() {
  const [c, setC] = useState(null);
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState("");

  const load = async () => {
    try {
      const r = await api.get("/admin/content/franchise");
      setC(r.data);
    } catch { setMsg("Failed to load content"); }
  };
  useEffect(() => { load(); }, []);

  const set = (k, v) => setC((p) => ({ ...p, [k]: v }));
  const setItem = (k, idx, field, val) => setC((p) => {
    const arr = [...(p[k] || [])];
    arr[idx] = { ...arr[idx], [field]: val };
    return { ...p, [k]: arr };
  });

  const save = async () => {
    setBusy(true); setMsg("");
    try {
      await api.put("/admin/content/franchise", c);
      setMsg("Saved successfully");
      setTimeout(() => setMsg(""), 2500);
    } catch { setMsg("Save failed"); }
    setBusy(false);
  };

  if (!c) return <div className="text-[#bfb393] text-sm">Loading…</div>;

  const Row = ({ label, k, type = "text" }) => (
    <div>
      <div className="text-[11px] uppercase tracking-wider text-[#a89878] mb-1">{label}</div>
      {type === "textarea" ? (
        <textarea
          value={c[k] || ""} rows={3}
          onChange={(e) => set(k, e.target.value)}
          className="w-full bg-[#0a0604] border border-[#3a2a14] rounded-md px-3 py-2 text-sm text-white"
          data-testid={`fr-${k}`}
        />
      ) : (
        <Input value={c[k] || ""} onChange={(e) => set(k, e.target.value)} className="w-full" data-testid={`fr-${k}`} />
      )}
    </div>
  );

  const Group = ({ title, children }) => (
    <div className="border border-[#3a2a14] rounded-xl p-5 bg-[#0d0905]">
      <h3 className="text-[#f4d089] font-display text-lg mb-4">{title}</h3>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">{children}</div>
    </div>
  );

  return (
    <div data-testid="franchise-content-view">
      <div className="flex items-center justify-between mb-5 flex-wrap gap-3">
        <div>
          <h2 className="font-display text-2xl text-white">Franchise Page Content</h2>
          <p className="text-xs text-[#a89878]">Edit every text on /franchise. Changes go live immediately after Save.</p>
        </div>
        <div className="flex gap-2 items-center">
          {msg && <span className="text-xs text-[#7ad99c]">{msg}</span>}
          <Btn onClick={save} disabled={busy} data-testid="fr-save-btn">{busy ? "Saving…" : "Save changes"}</Btn>
        </div>
      </div>

      <div className="space-y-5">
        <Group title="Hero">
          <Row label="Eyebrow tag" k="hero_eyebrow" />
          <Row label="Title — before highlight" k="hero_title_pre" />
          <Row label="Title — highlighted text" k="hero_title_highlight" />
          <Row label="Title — after highlight" k="hero_title_post" />
          <Row label="Subtitle" k="hero_subtitle" type="textarea" />
          <Row label="Primary CTA label" k="hero_cta_primary" />
          <Row label="Secondary CTA label" k="hero_cta_secondary" />
        </Group>

        <Group title="Hero Stats (4 cards)">
          {[1,2,3,4].map((n) => (
            <React.Fragment key={n}>
              <Row label={`Stat ${n} value`} k={`stat${n}_value`} />
              <Row label={`Stat ${n} label`} k={`stat${n}_label`} />
            </React.Fragment>
          ))}
        </Group>

        <Group title="Why Partner With Us">
          <Row label="Eyebrow" k="why_eyebrow" />
          <Row label="Heading" k="why_heading" />
          <Row label="Subtitle" k="why_subtitle" type="textarea" />
        </Group>
        <div className="border border-[#3a2a14] rounded-xl p-5 bg-[#0d0905]">
          <h3 className="text-[#f4d089] font-display text-lg mb-4">Why — Items (6)</h3>
          {(c.why_items || []).map((it, i) => (
            <div key={i} className="grid md:grid-cols-2 gap-3 mb-3">
              <Input value={it.title} onChange={(e) => setItem("why_items", i, "title", e.target.value)} placeholder="Title" />
              <Input value={it.desc}  onChange={(e) => setItem("why_items", i, "desc",  e.target.value)} placeholder="Description" />
            </div>
          ))}
        </div>

        <Group title="How It Works">
          <Row label="Eyebrow" k="how_eyebrow" />
          <Row label="Heading" k="how_heading" />
        </Group>
        <div className="border border-[#3a2a14] rounded-xl p-5 bg-[#0d0905]">
          <h3 className="text-[#f4d089] font-display text-lg mb-4">How — Steps (4)</h3>
          {(c.how_steps || []).map((it, i) => (
            <div key={i} className="grid md:grid-cols-2 gap-3 mb-3">
              <Input value={it.title} onChange={(e) => setItem("how_steps", i, "title", e.target.value)} placeholder="Title" />
              <Input value={it.desc}  onChange={(e) => setItem("how_steps", i, "desc",  e.target.value)} placeholder="Description" />
            </div>
          ))}
        </div>

        <Group title="Investment Block">
          <Row label="Amount label" k="inv_amount_label" />
          <Row label="Amount value" k="inv_amount_value" />
          <Row label="Amount note"  k="inv_amount_note" />
          <Row label="Space label"  k="inv_space_label" />
          <Row label="Space value"  k="inv_space_value" />
          <Row label="Space note"   k="inv_space_note" />
          <Row label="ROI label"    k="inv_roi_label" />
          <Row label="ROI value"    k="inv_roi_value" />
          <Row label="ROI note"     k="inv_roi_note" />
          <Row label="Footer note"  k="inv_footer_note" />
        </Group>

        <Group title="Contact / Form">
          <Row label="Form eyebrow"  k="form_eyebrow" />
          <Row label="Form heading"  k="form_heading" />
          <Row label="Form subtitle" k="form_subtitle" type="textarea" />
          <Row label="Contact phone" k="contact_phone" />
          <Row label="Contact email" k="contact_email" />
        </Group>
      </div>
    </div>
  );
}
/* ---------- Homepage content ---------- */
function HomeContentView() {
  const [c, setC] = useState(null);
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState("");

  const load = async () => {
    try {
      const r = await api.get("/admin/content/home");
      setC(r.data);
    } catch {
      setMsg("Failed to load content");
    }
  };
  useEffect(() => { load(); }, []);

  const set = (k, v) => setC((p) => ({ ...p, [k]: v }));

  const setFood = (idx, field, val) => {
    setC((p) => {
      const arr = [...(p.food_items || [])];
      arr[idx] = { ...arr[idx], [field]: val };
      return { ...p, food_items: arr };
    });
  };

  const fileToDataUrl = (file) =>
    new Promise((res, rej) => {
      const fr = new FileReader();
      fr.onload = () => res(fr.result);
      fr.onerror = rej;
      fr.readAsDataURL(file);
    });

  const uploadFoodImage = async (idx, file) => {
    if (!file) return;
    const data = await fileToDataUrl(file);
    setFood(idx, "img", data);
  };

  const save = async () => {
    setBusy(true); setMsg("");
    try {
      await api.put("/admin/content/home", c);
      setMsg("Saved successfully");
      setTimeout(() => setMsg(""), 2500);
    } catch {
      setMsg("Save failed");
    }
    setBusy(false);
  };

  if (!c) return <div className="text-[#bfb393] text-sm">Loading…</div>;

  return (
    <div data-testid="home-content-view">
      <div className="flex items-center justify-between mb-5 flex-wrap gap-3">
        <div>
          <h2 className="font-display text-2xl text-white">Homepage Content</h2>
          <p className="text-xs text-[#a89878]">Edit the status strip and the "Deliciously Curated Food" section. Changes go live immediately after Save.</p>
        </div>
        <div className="flex gap-2 items-center">
          {msg && <span className="text-xs text-[#7ad99c]" data-testid="home-save-msg">{msg}</span>}
          <Btn onClick={save} disabled={busy} data-testid="home-save-btn">{busy ? "Saving…" : "Save changes"}</Btn>
        </div>
      </div>

      <div className="space-y-5">
        {/* Status strip */}
        <div className="border border-[#3a2a14] rounded-xl p-5 bg-[#0d0905]">
          <h3 className="text-[#f4d089] font-display text-lg mb-4">Status Strip (4 lines)</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <AboutField label="Slots left text" value={c.strip_slots} onChange={(v) => set("strip_slots", v)} testid="f-strip-slots" />
            <AboutField label="Viewers text" value={c.strip_viewers} onChange={(v) => set("strip_viewers", v)} testid="f-strip-viewers" />
            <AboutField label="Bookings today text" value={c.strip_bookings} onChange={(v) => set("strip_bookings", v)} testid="f-strip-bookings" />
            <AboutField label="Rating text" value={c.strip_rating} onChange={(v) => set("strip_rating", v)} testid="f-strip-rating" />
          </div>
        </div>

        {/* Food section */}
        <div className="border border-[#3a2a14] rounded-xl p-5 bg-[#0d0905]">
          <h3 className="text-[#f4d089] font-display text-lg mb-4">Deliciously Curated Food</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
            <AboutField label="Section heading" value={c.food_heading} onChange={(v) => set("food_heading", v)} testid="f-food-heading" />
          </div>

          <div className="space-y-4">
            {(c.food_items || []).map((it, idx) => (
              <div key={idx} className="grid grid-cols-1 sm:grid-cols-[140px_1fr] gap-4 border border-[#3a2a14] rounded-lg p-4 bg-[#0a0604]">
                <div>
                  {it.img ? (
                    <img src={it.img} alt={it.title} className="w-[140px] h-[100px] object-cover rounded-md border border-[#3a2a14]" />
                  ) : (
                    <div className="w-[140px] h-[100px] rounded-md border border-[#3a2a14] flex items-center justify-center text-[10px] text-[#7a6c54]">No image</div>
                  )}
                  <label className="mt-2 inline-flex items-center gap-1 text-[11px] text-[#f4d089] cursor-pointer hover:underline">
                    <Upload size={12} /> Upload new
                    <input
                      type="file"
                      accept="image/*"
                      className="hidden"
                      data-testid={`f-food-upload-${idx}`}
                      onChange={(e) => uploadFoodImage(idx, e.target.files?.[0])}
                    />
                  </label>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  <AboutField label={`Item ${idx + 1} — title`} value={it.title} onChange={(v) => setFood(idx, "title", v)} testid={`f-food-title-${idx}`} />
                  <AboutField label={`Item ${idx + 1} — price`} value={it.price} onChange={(v) => setFood(idx, "price", v)} testid={`f-food-price-${idx}`} />
                  <div className="md:col-span-2">
                    <AboutField label={`Item ${idx + 1} — image URL (optional, overridden by upload)`} value={it.img} onChange={(v) => setFood(idx, "img", v)} testid={`f-food-img-${idx}`} />
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Trust stats (6) */}
        <div className="border border-[#3a2a14] rounded-xl p-5 bg-[#0d0905]">
          <h3 className="text-[#f4d089] font-display text-lg mb-4">Trust Stats (6 items)</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {(c.stats || []).map((s, i) => (
              <React.Fragment key={i}>
                <AboutField
                  label={`Stat ${i + 1} — value`}
                  value={s.v}
                  onChange={(v) => {
                    const arr = [...(c.stats || [])];
                    arr[i] = { ...arr[i], v };
                    set("stats", arr);
                  }}
                  testid={`f-home-stat-v-${i}`}
                />
                <AboutField
                  label={`Stat ${i + 1} — label`}
                  value={s.t}
                  onChange={(v) => {
                    const arr = [...(c.stats || [])];
                    arr[i] = { ...arr[i], t: v };
                    set("stats", arr);
                  }}
                  testid={`f-home-stat-t-${i}`}
                />
              </React.Fragment>
            ))}
          </div>
        </div>

        {/* Transparent Billing */}
        <div className="border border-[#3a2a14] rounded-xl p-5 bg-[#0d0905]">
          <h3 className="text-[#f4d089] font-display text-lg mb-4">Transparent Billing</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="md:col-span-2">
              <AboutField label="Section heading" value={c.billing_heading} onChange={(v) => set("billing_heading", v)} testid="f-billing-heading" />
            </div>
            <AboutField label="Card 1 — title" value={c.billing_card1_title} onChange={(v) => set("billing_card1_title", v)} testid="f-billing-c1-title" />
            <AboutField label="Card 1 — subtitle" value={c.billing_card1_sub} onChange={(v) => set("billing_card1_sub", v)} testid="f-billing-c1-sub" />
            <div className="md:col-span-2">
              <AboutField label="Card 1 — body" value={c.billing_card1_body} onChange={(v) => set("billing_card1_body", v)} testid="f-billing-c1-body" />
            </div>
            <AboutField label="Card 2 — title" value={c.billing_card2_title} onChange={(v) => set("billing_card2_title", v)} testid="f-billing-c2-title" />
            <AboutField label="Card 2 — subtitle" value={c.billing_card2_sub} onChange={(v) => set("billing_card2_sub", v)} testid="f-billing-c2-sub" />
            <div className="md:col-span-2">
              <AboutField label="Card 2 — body" value={c.billing_card2_body} onChange={(v) => set("billing_card2_body", v)} testid="f-billing-c2-body" />
            </div>
            <div className="md:col-span-2">
              <AboutField label="Footer line" value={c.billing_footer} onChange={(v) => set("billing_footer", v)} testid="f-billing-footer" />
            </div>
          </div>
        </div>

        {/* Trust row (3 items, 2 lines each) */}
        <div className="border border-[#3a2a14] rounded-xl p-5 bg-[#0d0905]">
          <h3 className="text-[#f4d089] font-display text-lg mb-4">Trust Row (under billing)</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <AboutField label="Item 1 — line 1" value={c.trust1_l1} onChange={(v) => set("trust1_l1", v)} testid="f-trust1-l1" />
            <AboutField label="Item 1 — line 2" value={c.trust1_l2} onChange={(v) => set("trust1_l2", v)} testid="f-trust1-l2" />
            <AboutField label="Item 2 — line 1" value={c.trust2_l1} onChange={(v) => set("trust2_l1", v)} testid="f-trust2-l1" />
            <AboutField label="Item 2 — line 2" value={c.trust2_l2} onChange={(v) => set("trust2_l2", v)} testid="f-trust2-l2" />
            <AboutField label="Item 3 — line 1" value={c.trust3_l1} onChange={(v) => set("trust3_l1", v)} testid="f-trust3-l1" />
            <AboutField label="Item 3 — line 2" value={c.trust3_l2} onChange={(v) => set("trust3_l2", v)} testid="f-trust3-l2" />
          </div>
        </div>

        {/* Promo badge */}
        <div className="border border-[#3a2a14] rounded-xl p-5 bg-[#0d0905]">
          <h3 className="text-[#f4d089] font-display text-lg mb-4">Promo Badge</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <AboutField label="Code line (top)" value={c.promo_code} onChange={(v) => set("promo_code", v)} testid="f-promo-code" />
            
        




  
  



            <AboutField label="Offer line (bottom)" value={c.promo_label} onChange={(v) => set("promo_label", v)} testid="f-promo-label" />
          </div>
        </div>

        {/* Franchise Opportunity card */}
        <div className="border border-[#3a2a14] rounded-xl p-5 bg-[#0d0905]">
          <h3 className="text-[#f4d089] font-display text-lg mb-4">Franchise Opportunity Card</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <AboutField label="Heading"                       value={c.franchise_heading}       onChange={(v) => set("franchise_heading", v)}       testid="f-franchise-heading" />
            <AboutField label="Amount (e.g. ₹5-10L)"          value={c.franchise_amount}        onChange={(v) => set("franchise_amount", v)}        testid="f-franchise-amount" />
            <div className="md:col-span-2">
              <AboutField label="Subtitle"                     value={c.franchise_subtitle}     onChange={(v) => set("franchise_subtitle", v)}      testid="f-franchise-subtitle" />
            </div>
            <AboutField label="Amount suffix (e.g. Monthly Potential*)" value={c.franchise_amount_suffix} onChange={(v) => set("franchise_amount_suffix", v)} testid="f-franchise-amount-suffix" />
            <AboutField label="Button label"                   value={c.franchise_btn_label}    onChange={(v) => set("franchise_btn_label", v)}     testid="f-franchise-btn-label" />
            <div className="md:col-span-2">
              <AboutField label="Button URL (e.g. /franchise)" value={c.franchise_btn_url}      onChange={(v) => set("franchise_btn_url", v)}       testid="f-franchise-btn-url" />
            </div>
          </div>
        </div>

        {/* List Your Space card */}
        <div className="border border-[#3a2a14] rounded-xl p-5 bg-[#0d0905]">
          <h3 className="text-[#f4d089] font-display text-lg mb-4">List Your Space Card</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <AboutField label="Heading"                          value={c.list_space_heading}    onChange={(v) => set("list_space_heading", v)}    testid="f-list-space-heading" />
            <AboutField label="Title (e.g. Earn with CineVibe)"  value={c.list_space_title}      onChange={(v) => set("list_space_title", v)}      testid="f-list-space-title" />
            <div className="md:col-span-2">
              <AboutField label="Subtitle"                       value={c.list_space_subtitle}   onChange={(v) => set("list_space_subtitle", v)}   testid="f-list-space-subtitle" />
            </div>
            <AboutField label="Button label"                     value={c.list_space_btn_label}  onChange={(v) => set("list_space_btn_label", v)}  testid="f-list-space-btn-label" />
            <div className="md:col-span-2">
              <AboutField label="Button URL (e.g. /list-property)" value={c.list_space_btn_url}  onChange={(v) => set("list_space_btn_url", v)}    testid="f-list-space-btn-url" />
            </div>
          </div>
        </div>

        {/* FAQs (editable list) */}
        <div className="border border-[#3a2a14] rounded-xl p-5 bg-[#0d0905]">
          <div className="flex items-center justify-between mb-4 flex-wrap gap-2">
            <h3 className="text-[#f4d089] font-display text-lg">Frequently Asked Questions</h3>
            <Btn
              onClick={() => set("faqs", [...(c.faqs || []), { q: "", a: "" }])}
              data-testid="faq-add-btn"
            >
              + Add FAQ
            </Btn>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-5">
            <AboutField
              label="Section heading"
              value={c.faq_heading}
              onChange={(v) => set("faq_heading", v)}
              testid="f-faq-heading"
            />
          </div>

          <div className="space-y-4">
            {(c.faqs || []).map((it, idx) => (
              <div key={idx} className="border border-[#3a2a14] rounded-lg p-4 bg-[#0a0604]">
                <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
                  <span className="text-[11px] tracking-widest uppercase text-[#a89878]">FAQ #{idx + 1}</span>
                  <div className="flex items-center gap-2">
                    <button
                      type="button"
                      onClick={() => {
                        if (idx === 0) return;
                        const arr = [...(c.faqs || [])];
                        [arr[idx - 1], arr[idx]] = [arr[idx], arr[idx - 1]];
                        set("faqs", arr);
                      }}
                      className="text-xs text-[#bfb393] hover:text-[#f4d089] disabled:opacity-30"
                      disabled={idx === 0}
                      data-testid={`faq-up-${idx}`}
                    >↑ Up</button>
                    <button
                      type="button"
                      onClick={() => {
                        const arr = [...(c.faqs || [])];
                        if (idx === arr.length - 1) return;
                        [arr[idx + 1], arr[idx]] = [arr[idx], arr[idx + 1]];
                        set("faqs", arr);
                      }}
                      className="text-xs text-[#bfb393] hover:text-[#f4d089] disabled:opacity-30"
                      disabled={idx === (c.faqs || []).length - 1}
                      data-testid={`faq-down-${idx}`}
                    >↓ Down</button>
                    <button
                      type="button"
                      onClick={() => {
                        if (!window.confirm(`Delete FAQ #${idx + 1}?`)) return;
                        const arr = [...(c.faqs || [])];
                        arr.splice(idx, 1);
                        set("faqs", arr);
                      }}
                      className="text-xs text-red-400 hover:text-red-300"
                      data-testid={`faq-delete-${idx}`}
                    >Delete</button>
                  </div>
                </div>
                <div className="grid grid-cols-1 gap-3">
                  <AboutField
                    label="Question"
                    value={it.q}
                    onChange={(v) => {
                      const arr = [...(c.faqs || [])];
                      arr[idx] = { ...arr[idx], q: v };
                      set("faqs", arr);
                    }}
                    testid={`f-faq-q-${idx}`}
                  />
                  <AboutField
                    label="Answer"
                    value={it.a}
                    onChange={(v) => {
                      const arr = [...(c.faqs || [])];
                      arr[idx] = { ...arr[idx], a: v };
                      set("faqs", arr);
                    }}
                    multiline
                    testid={`f-faq-a-${idx}`}
                  />
                </div>
              </div>
            ))}
            {(c.faqs || []).length === 0 && (
              <div className="text-center text-xs text-[#a89878] py-6 border border-dashed border-[#3a2a14] rounded-md">
                No FAQs yet. Click <span className="text-[#f4d089]">+ Add FAQ</span> to create the first one.
              </div>
            )}
          </div>
        </div>

        <div className="flex justify-end pt-2">
          <Btn onClick={save} disabled={busy} data-testid="home-save-btn-bottom">{busy ? "Saving…" : "Save changes"}</Btn>
        </div>
      </div>
    </div>
  );
}                  



/* ---------- Shell ---------- */
export default function Admin() {
  const [authed, setAuthed] = useState(!!localStorage.getItem(TOKEN_KEY));
  const [branchAuthed, setBranchAuthed] = useState(!!localStorage.getItem(BRANCH_TOKEN_KEY));
  const [tab, setTab] = useState("dashboard");
  const [cities, setCities] = useState([]);
  const [branches, setBranches] = useState([]);
  const [rooms, setRooms] = useState([]);
  const [showChangePw, setShowChangePw] = useState(false);

  const load = async () => {
    try {
      const [c, b, r] = await Promise.all([
        api.get("/admin/cities"),
        api.get("/admin/branches"),
        api.get("/admin/rooms"),
      ]);
      setCities(c.data); setBranches(b.data); setRooms(r.data);
    } catch (e) {
      if (e.response?.status === 401) {
        localStorage.removeItem(TOKEN_KEY);
        setAuthed(false);
      }
    }
  };

  useEffect(() => { if (authed) load(); }, [authed]);

  const branchLogout = () => {
    localStorage.removeItem(BRANCH_TOKEN_KEY);
    localStorage.removeItem(BRANCH_META_KEY);
    setBranchAuthed(false);
  };

  if (branchAuthed) return <BranchPanel onLogout={branchLogout} />;
  if (!authed) return <Login onSuper={() => setAuthed(true)} onBranch={() => setBranchAuthed(true)} />;

  const logout = () => { localStorage.removeItem(TOKEN_KEY); setAuthed(false); };
  

  const groups = [
    { items: [{ k: "dashboard", t: "Dashboard", i: <LayoutDashboard size={16} /> }] },
    { label: "Manage", items: [
      { k: "bookings", t: "Bookings", i: <CalendarCheck size={16} /> },
      { k: "calendar", t: "Calendar / Slots", i: <CalendarDays size={16} /> },
      { k: "rooms", t: "Rooms", i: <DoorOpen size={16} /> },
      { k: "addons", t: "Add-ons", i: <Sparkles size={16} /> },
      { k: "coupons", t: "Coupons", i: <Gift size={16} /> }, 
      { k: "experiences", t: "Experiences", i: <Compass size={16} /> },
      { k: "packages", t: "Packages", i: <Gift size={16} /> },
      { k: "food", t: "Food (Billing Software)", i: <UtensilsCrossed size={16} /> },
    ]},
    { label: "Location", items: [
      { k: "branches", t: "Branches", i: <Building2 size={16} /> },
      { k: "cities", t: "Cities", i: <MapPin size={16} /> },
    ]},
    { label: "Billing & Finance", items: [
      { k: "billing", t: "Billing & Invoices", i: <Receipt size={16} /> },
      { k: "payments", t: "Payments", i: <Wallet size={16} /> },
      { k: "reports", t: "Reports", i: <BarChart3 size={16} /> },
    ]},
    { label: "Leads & Growth", items: [
      { k: "franchise", t: "Franchise Leads", i: <Building2 size={16} /> },
      { k: "property", t: "Property Leads", i: <HomeIcon size={16} /> },
      { k: "contact", t: "Contact Messages", i: <FileText size={16} /> },
    ]},
    { label: "Settings", items: [
      { k: "users", t: "Users & Roles", i: <Users size={16} /> },
      { k: "settings", t: "Settings", i: <SettingsIcon size={16} /> },
      { k: "home_content", t: "Homepage", i: <HomeIcon size={16} /> },
      { k: "about", t: "About Page", i: <FileText size={16} /> },
      { k: "list_property", t: "List-Your-Space Page", i: <HomeIcon size={16} /> },
      { k: "franchise_content", t: "Franchise Page", i: <FileText size={16} /> },
      { k: "contact_info", t: "Contact Info", i: <Phone size={16} /> },
      { k: "activity", t: "Activity Logs", i: <History size={16} /> },
      { k: "backup", t: "Export", i: <Database size={16} /> },             
    ]},
  ];
  const allTabs = groups.flatMap((g) => g.items);

  const NavBtn = ({ t }) => (
    <button
      key={t.k}
      onClick={() => setTab(t.k)}
      data-testid={`tab-${t.k}`}
      className={`w-full flex items-center justify-between gap-2 px-3 py-2 rounded-md text-sm transition ${
        tab === t.k ? "bg-[#1a0f06] text-[#f4d089] border border-[#d4a574]/30" : "text-[#bfb393] hover:bg-[#1a0f06]"
      }`}
    >
      <span className="flex items-center gap-2">{t.i} {t.t}</span>
      <span className="text-[#a89878]">›</span>
    </button>
  );

  return (
    <div className="min-h-screen bg-[#0a0604] text-white flex">
      <aside className="w-64 border-r border-[#3a2a14] p-5 hidden md:block overflow-y-auto max-h-screen">
        <div className="font-display text-xl text-[#f4d089] mb-1">CineVibe</div>
        <div className="text-[10px] tracking-widest text-[#a89878] uppercase mb-6">Super Admin</div>
        <nav className="space-y-4">
          {groups.map((g, gi) => (
            <div key={gi}>
              {g.label && <div className="text-[10px] tracking-widest text-[#a89878] uppercase mb-2 px-1">{g.label}</div>}
              <div className="space-y-1">
                {g.items.map((t) => <NavBtn key={t.k} t={t} />)}
              </div>
            </div>
          ))}
        </nav>
        <button onClick={() => setShowChangePw(true)} className="mt-6 flex items-center gap-2 text-xs text-[#a89878] hover:text-[#f4d089]" data-testid="change-pw-btn">
  Change Password
</button>
        <button onClick={logout} className="mt-8 flex items-center gap-2 text-xs text-[#a89878] hover:text-red-400" data-testid="logout-btn">
          <LogOut size={14} /> Logout
        </button>
      </aside>
      <main className="flex-1 p-6 md:p-8 overflow-x-hidden">
        <div className="md:hidden flex gap-2 overflow-x-auto mb-5 pb-2">
          {allTabs.map((t) => (
            <button key={t.k} onClick={() => setTab(t.k)} className={`px-3 py-2 rounded-md text-xs whitespace-nowrap ${tab === t.k ? "bg-[#1a0f06] text-[#f4d089]" : "text-[#bfb393]"}`}>{t.t}</button>
          ))}
          <button onClick={logout} className="px-3 py-2 text-xs text-red-400">Logout</button>
        </div>
        {tab === "dashboard" && <StatsView cities={cities} branches={branches} setTab={setTab} />}
        {tab === "bookings" && <BookingsView cities={cities} branches={branches} rooms={rooms} />}
        {tab === "calendar" && <CalendarSlotsView branches={branches} rooms={rooms} />}
        {tab === "rooms" && <RoomsView branches={branches} rooms={rooms} reload={load} />}
        {tab === "addons" && <AddOnsView />}
        {tab === "coupons" && <CouponsView />} 
        {tab === "experiences" && <ExperiencesView />}
        {tab === "packages" && <PackagesView />}
        {tab === "food" && <FoodPetpoojaView cities={cities} branches={branches} />}
        {tab === "branches" && <BranchesView cities={cities} branches={branches} reload={load} />}
        {tab === "cities" && <CitiesView cities={cities} reload={load} />}
        {tab === "billing" && <BillingInvoicesView branches={branches} />}
        {tab === "payments" && <PaymentsView branches={branches} />}
        {tab === "reports" && <ReportsView cities={cities} branches={branches} />}
        {tab === "users" && <ManagersView branches={branches} />}
        {tab === "settings" && <SettingsView />}
        {tab === "home_content" && <HomeContentView />}
        {tab === "about" && <AboutContentView />}
        {tab === "list_property" && <ListPropertyContentView />}
        {tab === "franchise_content" && <FranchiseContentView />}
        {tab === "contact_info" && <ContactInfoView />}
        {tab === "activity" && <ActivityLogsView branches={branches} />}
        {tab === "backup" && <BackupView />}
        {tab === "franchise" && <FranchiseLeadsView />}
        {tab === "property" && <PropertyLeadsView />}
        {tab === "contact" && <ContactMessagesView />}

        <ChangePasswordModal
  open={showChangePw}
  onClose={() => setShowChangePw(false)}
  onChanged={() => { /* token already refreshed in modal */ }}
/>
      </main>
    </div>
  );
}
